import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { ViewMode, ToastMessage } from '../types'
import { 
  getResearchStatus, 
  getResearchResult, 
  checkApiHealth,
  getWebSocketManager,
  updateResearchPrivacy,
  recordResearchView,
  getAuthHeader,
  API_BASE_URL,
} from '../lib/api'
import {
  startIntelligenceRun,
  createIntelligenceEventSource,
  confirmIntelligencePlan,
  fetchIntelligenceStatus,
  cancelIntelligenceReport,
  fetchIntelligencePlan,
  IntelligenceApiError,
} from '../lib/intelligenceApi'
import { researchStatusService } from '../lib/researchStatusService'
import { fetchUserResearchWithFallback } from '../lib/apiWithFallback'
import { apiCache } from '../lib/cache'
import type { ResearchListCounts } from '../lib/optimizedApi'
import { useAuth } from '../contexts/AuthContext'
import UpgradeModal from '../components/billing/UpgradeModal'
import PageHeader from '../components/PageHeader'
import ErrorBoundary from '../components/shared/ErrorBoundary'
import ResearchModal from '../components/ResearchModal'
import ResearchResultViewer from '../components/research/ResearchResultViewer'
import IntelligenceProgressOverlay from '../components/research/IntelligenceProgressOverlay'
import IntelligenceReportCockpit, {
  buildIntelligenceReportStudy,
  normalizeIntelligenceReportData,
  type IntelligenceReportData,
} from '../components/cockpit/IntelligenceReportCockpit'
import { getResearchTitle } from '../lib/utils'
import { LoadingAnimation } from '../components/ui/LoadingAnimation'
import { analytics, useScreenEngagement } from '../lib/analytics'
import { IntelligencePlanModal } from '../components/research/IntelligencePlanModal'
import { InlineResearchFlow } from '../components/research/InlineResearchFlow'
import OutOfScopeModal from '../components/research/OutOfScopeModal'
import { isLogoutInProgress } from '../lib/authSession'
import { filterProductionDiscoveryItems } from '../lib/reportDiscovery'
import { MOBILE_WELCOME_FEATURES, SparklesIcon, useAutoScrollingTicker } from '../components/onboarding/MobileWelcomeShared'
import type { ResearchPlan, ResearchDepth } from '@/types/intelligence'
import type { BillingStatus } from '../lib/billingApi'
import {
  collectRecoverablePendingPlans,
  normalizeResearchPlan,
  type RecoverablePendingPlanSummary,
} from '../lib/pendingPlanRecovery'
import {
  getIntelligenceProgressLabel,
  getIntelligenceProgressPhase,
  getIntelligenceProgressState,
} from '../lib/intelligenceProgressState'

interface ResearchPageProps {
  viewMode: ViewMode
  onShowToast: (toast: Omit<ToastMessage, 'id'>) => void
  hasFloatingAudio?: boolean
  onModalToggle?: (hide: boolean) => void
  onNavigateToProfile?: (userEmail: string) => void
  onArticleOpenChange?: (isOpen: boolean) => void
}

export interface ResearchStudy {
  id: string
  query: string
  title?: string  // Title at top level for optimized access
  summary?: string  // Summary at top level for optimized access
  category?: string  // Category from AI categorization
  model: 'chatgpt' | 'gemini' | 'claude'
  status: 'pending' | 'queued' | 'in_progress' | 'interrupted' | 'podcast_pending' | 'completed' | 'failed' | 'planning' | 'awaiting_confirmation' | 'cancelled'
  createdAt: Date
  completedAt?: Date
  progress?: number
  etaSeconds?: number
  task_id?: string
  image_url?: string
  statusMessage?: string
  currentPhase?: string  // Real phase from backend: PLAN, COLLECT, DEEP_RESEARCH, EXTRACT, etc.
  planRetryCount?: number
  planRetryMax?: number
  depth?: ResearchDepth
  isIntelligenceReport?: boolean
  intelligenceReportId?: string
  researchId?: number  // Numeric ID for Save/Share APIs
  podcast_status?: 'missing' | 'queued' | 'processing' | 'ready' | 'failed'
  podcast_audio_ready?: boolean
  podcast_duration_seconds?: number
  podcast_error_message?: string
  isSaved?: boolean
  results?: {
    title: string
    summary: string
    content: string
    keyFindings: string[]
    key_findings?: string[]  // Backend sends this format
    implications: string[]
    sources: string[]
    report_text: string
  }
  isPublic?: boolean // Added for privacy toggle
  author?: {
    id?: string
    name: string
    email?: string
    avatar_url?: string
  }
}

type ResearchPageTab = 'completed' | 'in_progress' | 'drafts'

type ResearchTabDefinition = {
  value: ResearchPageTab
  label: string
  mobileLabel?: string
}

const RESEARCH_PAGE_SIZE = 50
const DEFAULT_RESEARCH_COUNTS: ResearchListCounts = {
  completed: 0,
  in_progress: 0,
  drafts: 0,
}
const RESEARCH_TAB_DEFINITIONS: ResearchTabDefinition[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'in_progress', label: 'In Progress', mobileLabel: 'Active' },
  { value: 'drafts', label: 'Drafts' },
]

type PendingPlanState = {
  reportId: string
  plan: ResearchPlan
  planPreview: string
  query: string
  isPublic: boolean
  depth: ResearchDepth
}

type PlanGenerationResult = {
  reportId: string
  plan: ResearchPlan
  planPreview: string
}

const PLAN_WAIT_CANCELLED_MESSAGE = 'Research plan cancelled.'
const ACTIVE_INTELLIGENCE_STATUSES: ResearchStudy['status'][] = [
  'pending',
  'queued',
  'in_progress',
  'interrupted',
  'podcast_pending',
]

type ActivePlanWait = {
  requestId: number
  reportId: string
  eventSource: EventSource | null
  timeoutId: ReturnType<typeof setTimeout> | null
  reject: ((reason: Error) => void) | null
}

type PendingPlanStart = {
  requestId: number
  cancelled: boolean
  reportId: string | null
}

type IntelligenceRunSummary = {
  reportId: string
  planSummary: string
  personaMode: string
  status: string
  shipScore?: number
  query: string
  createdAt?: string // ISO timestamp for cleanup of stale runs
}

const slugifyTopic = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 48)

const inferPersonaMode = (prompt: string): string => {
  const text = prompt.toLowerCase()
  if (/(earnings|valuation|equity|investor|ipo|stock)/.test(text)) return 'equity_research'
  if (/(startup|venture|funding|seed|pitch|series)/.test(text)) return 'vc_partner'
  if (/(lab|clinical|trial|biotech|molecule|therapeutic|pi)/.test(text)) return 'scientific_lab_pi'
  if (/(policy|regulation|law|bill|compliance|legislation)/.test(text)) return 'policy_director'
  if (/(intel|geopolitic|conflict|threat|military|surveillance)/.test(text)) return 'intelligence_officer'
  return 'management_consultant'
}

function MobileResearchWelcomeState({
  onCreateResearch,
  disabled,
}: {
  onCreateResearch: () => void
  disabled: boolean
}) {
  const {
    tickerRef,
    handleTickerScroll,
    pauseTicker,
    resumeTickerSoon,
  } = useAutoScrollingTicker()

  return (
    <div
      data-testid="research-first-time-empty-state"
      className="box-border h-[calc(100svh-4rem)] overflow-hidden bg-gray-50 px-5 pt-[calc(env(safe-area-inset-top,0px)+1rem)] pb-[calc(env(safe-area-inset-bottom,0px)+7rem)] dark:bg-gray-900"
    >
      <style>{`
        @keyframes researchWelcomeGraphicFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        @keyframes researchWelcomeOuterPulse {
          0%, 100% { transform: scale(1); opacity: 0.82; }
          50% { transform: scale(1.06); opacity: 0.96; }
        }

        @keyframes researchWelcomeInnerPulse {
          0%, 100% { transform: scale(1); opacity: 0.95; }
          50% { transform: scale(0.94); opacity: 0.72; }
        }

        @keyframes researchWelcomeSparkleTwinkle {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
          50% { transform: scale(1.08) rotate(4deg); opacity: 0.88; }
        }

        .research-welcome-strip::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="mx-auto flex h-full max-w-md items-center justify-center">
        <div className="flex w-full flex-col items-center text-center">
          <div
            className="relative mb-6 flex h-32 w-32 items-center justify-center sm:h-36 sm:w-36"
            style={{ animation: 'researchWelcomeGraphicFloat 4.8s ease-in-out infinite' }}
          >
            <div
              className="absolute inset-0 rounded-full bg-slate-100/80 dark:bg-slate-800/70"
              style={{ animation: 'researchWelcomeOuterPulse 6s ease-in-out infinite' }}
            />
            <div
              className="absolute inset-5 rounded-full bg-slate-50 dark:bg-slate-800/45"
              style={{ animation: 'researchWelcomeInnerPulse 5.2s ease-in-out infinite' }}
            />
            <div className="relative flex h-[76px] w-[76px] items-center justify-center rounded-[24px] bg-white text-slate-900 shadow-[0_18px_45px_rgba(15,23,42,0.10)] ring-1 ring-slate-100 dark:bg-slate-900 dark:text-white dark:ring-slate-800">
              <div style={{ animation: 'researchWelcomeSparkleTwinkle 3.2s ease-in-out infinite' }}>
                <SparklesIcon />
              </div>
            </div>
          </div>

          <h2 className="whitespace-nowrap text-[clamp(1.6rem,6.9vw,2.45rem)] font-semibold leading-[1.05] tracking-[-0.04em] text-slate-900 dark:text-white">
            Start Your Research Journey
          </h2>
          <p className="mt-3 max-w-[18rem] text-[1.02rem] leading-7 text-slate-500 dark:text-slate-400">
            Discover insights powered by AI. Your first research is just a tap away.
          </p>

          <button
            type="button"
            onClick={onCreateResearch}
            disabled={disabled}
            className="mx-auto mt-6 flex min-h-[56px] w-full max-w-[24rem] items-center justify-center gap-2.5 rounded-[22px] bg-slate-950 px-5 text-[0.98rem] font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.18)] transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200 dark:disabled:bg-slate-700 dark:disabled:text-slate-300"
          >
            <svg className="h-[18px] w-[18px] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="whitespace-nowrap">{disabled ? 'Generating...' : 'Create Your First Research'}</span>
          </button>

          <div
            ref={tickerRef}
            data-testid="research-welcome-feature-ticker"
            role="region"
            aria-label="Swipe to explore feature highlights"
            tabIndex={0}
            onScroll={handleTickerScroll}
            onPointerDown={pauseTicker}
            onPointerUp={resumeTickerSoon}
            onPointerCancel={resumeTickerSoon}
            onTouchStart={pauseTicker}
            onTouchEnd={resumeTickerSoon}
            onMouseEnter={pauseTicker}
            onMouseLeave={resumeTickerSoon}
            className="research-welcome-strip mt-5 w-full overflow-x-auto touch-pan-x [scrollbar-width:none]"
            style={{ msOverflowStyle: 'none' }}
          >
            <div
              className="flex w-max items-center whitespace-nowrap text-[0.92rem] font-medium text-slate-500 dark:text-slate-400"
            >
              {[0, 1].map(copyIndex => (
                <div
                  key={copyIndex}
                  className="flex shrink-0 items-center justify-center gap-3 px-4"
                  aria-hidden={copyIndex === 1}
                >
                  {MOBILE_WELCOME_FEATURES.map((feature, featureIndex) => (
                    <React.Fragment key={`${copyIndex}-${feature.key}`}>
                      <div className="inline-flex items-center gap-2">
                        {feature.icon}
                        <span>{feature.label}</span>
                      </div>
                      {featureIndex < MOBILE_WELCOME_FEATURES.length - 1 && (
                        <span className="h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-600" aria-hidden="true" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ResearchPage({
  viewMode,
  onShowToast,
  hasFloatingAudio = false,
  onModalToggle,
  onNavigateToProfile,
  onArticleOpenChange,
}: ResearchPageProps) {
  // Wrap the entire component content in error boundary
  return (
    <ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Unable to load the research page. Please refresh and try again.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      }
    >
      <ResearchPageContent
        viewMode={viewMode}
        onShowToast={onShowToast}
        hasFloatingAudio={hasFloatingAudio}
        onModalToggle={onModalToggle}
        onNavigateToProfile={onNavigateToProfile}
        onArticleOpenChange={onArticleOpenChange}
      />
    </ErrorBoundary>
  )
}

export {}

// Helper to check localStorage usage
const checkLocalStorageUsage = () => {
  const debugMode = localStorage.getItem('researchPageDebugMode') === 'true'

  if (debugMode) {
    let totalSize = 0
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        const item = localStorage.getItem(key)
        if (item) {
          totalSize += item.length + key.length
        }
      }
    }
    // Debug: Total localStorage usage
    // console.log(`Total localStorage usage: ${(totalSize / 1024 / 1024).toFixed(2)} MB`)
  }
}

function ResearchPageContent({
  viewMode,
  onShowToast,
  hasFloatingAudio = false,
  onModalToggle,
  onNavigateToProfile,
  onArticleOpenChange,
}: ResearchPageProps) {
  // Add debugging for error boundary
  // console.log('ResearchPageContent rendering with viewMode:', viewMode)
  
  // Debug mode flag - can be toggled via localStorage
  const debugMode = localStorage.getItem('researchPageDebugMode') === 'true'
  
  // Screen engagement tracking
  const engagementTracking = useScreenEngagement({ screenName: 'research' })
  const trackedStudyCardIdsRef = useRef<Set<string>>(new Set())
  const isMobile = viewMode === 'mobile'
  
  const [studies, setStudies] = useState<ResearchStudy[]>([])
  const [isStartingResearch, setIsStartingResearch] = useState(false)
  const [statusFilter, setStatusFilter] = useState<ResearchPageTab>('completed')
  const [apiHealthy, setApiHealthy] = useState<boolean>(true)
  const [selectedStudy, setSelectedStudy] = useState<ResearchStudy | null>(null)
  const [isLoadingStudyDetails, setIsLoadingStudyDetails] = useState(false)
  // State for intelligence reports with cockpit data
  const [intelligenceReportData, setIntelligenceReportData] = useState<IntelligenceReportData | null>(null)
  const [showCockpit, setShowCockpit] = useState(false)
  const [showLegacyViewer, setShowLegacyViewer] = useState(false)
  const [showProgressOverlay, setShowProgressOverlay] = useState(false)
  const [isCancellingProgressStudy, setIsCancellingProgressStudy] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [outOfScope, setOutOfScope] = useState<{ visible: boolean; message: string }>({
    visible: false,
    message: '',
  })
  const [willBillNotice, setWillBillNotice] = useState<null | { priceCents: number }>(null)
  const [billingPlan, setBillingPlan] = useState<'free' | 'pro2' | 'pro5'>('free')
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null)
  const [pendingPlan, setPendingPlan] = useState<PendingPlanState | null>(null)
  const [recoverablePendingPlans, setRecoverablePendingPlans] = useState<RecoverablePendingPlanSummary[]>([])
  const [recoveringPendingPlanId, setRecoveringPendingPlanId] = useState<string | null>(null)
  const [statusCounts, setStatusCounts] = useState<ResearchListCounts>(DEFAULT_RESEARCH_COUNTS)
  const [hasMoreStudies, setHasMoreStudies] = useState(false)
  const [loadedServerItemCount, setLoadedServerItemCount] = useState(0)
  const [isLoadingMoreStudies, setIsLoadingMoreStudies] = useState(false)
  const [planConfirming, setPlanConfirming] = useState(false)
  const [intelligenceRuns, setIntelligenceRuns] = useState<IntelligenceRunSummary[]>(() => {
    if (typeof window === 'undefined') return []
    try {
      const stored = localStorage.getItem('insightIntelligenceRuns')
      if (!stored) return []
      const parsed = JSON.parse(stored) as IntelligenceRunSummary[]
      // Filter out stale planning runs (older than 1 hour) to prevent infinite polling,
      // and expire EVERY stored run after 24 hours — entries left at in_progress/queued
      // because a tab was closed mid-run otherwise resurface as "active" forever.
      const oneHourAgo = Date.now() - 60 * 60 * 1000
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000
      return parsed.filter(run => {
        const createdAt = run.createdAt ? new Date(run.createdAt).getTime() : 0
        if (run.status === 'planning' || run.status === 'awaiting_confirmation') {
          // Remove if created more than 1 hour ago
          return createdAt > oneHourAgo
        }
        return createdAt > oneDayAgo
      })
    } catch {
      return []
    }
  })
  // State for pending research from landing page
  const [pendingLandingResearch, setPendingLandingResearch] = useState<{
    query: string
    depth: ResearchDepth
    isPublic: boolean
  } | null>(null)

  useEffect(() => {
    analytics.trackPageViewed({
      page: 'research',
      referrer: document.referrer,
      view_mode: viewMode,
    })
  }, [viewMode])
  
  // Check for pending research from landing page on mount AND listen for event
  useEffect(() => {
    // Check sessionStorage on mount
    const checkSessionStorage = () => {
      try {
        const stored = sessionStorage.getItem('autoStartResearch')
        if (stored) {
          const data = JSON.parse(stored)
          if (data.query) {
            setPendingLandingResearch({
              query: data.query,
              depth: data.depth || 'maximum',
              isPublic: data.isPublic || false
            })
            return true
          }
        }
      } catch (error) {
        console.warn('Failed to parse pending research:', error)
      }
      return false
    }
    
    // Check immediately
    checkSessionStorage()
    
    // Also listen for event (in case sessionStorage was set after mount)
    const handlePendingResearch = (e: CustomEvent) => {
      if (e.detail?.query) {
        setPendingLandingResearch({
          query: e.detail.query,
          depth: e.detail.depth || 'maximum',
          isPublic: e.detail.isPublic || false
        })
      }
    }
    
    window.addEventListener('pendingResearchReady', handlePendingResearch as EventListener)
    return () => window.removeEventListener('pendingResearchReady', handlePendingResearch as EventListener)
  }, [])
  
  // Callback when auto-start is consumed
  const handleAutoStartConsumed = useCallback(() => {
    sessionStorage.removeItem('autoStartResearch')
    setPendingLandingResearch(null)
  }, [])

  const clearActivePlanWait = useCallback(() => {
    const activePlanWait = activePlanWaitRef.current
    if (!activePlanWait) return

    if (activePlanWait.timeoutId) {
      clearTimeout(activePlanWait.timeoutId)
    }
    if (
      activePlanWait.eventSource &&
      typeof (activePlanWait.eventSource as { close?: unknown }).close === 'function'
    ) {
      activePlanWait.eventSource.close()
    }
    activePlanWaitRef.current = null
  }, [])

  const abortActivePlanWait = useCallback((error: Error = new Error(PLAN_WAIT_CANCELLED_MESSAGE)) => {
    const activePlanWait = activePlanWaitRef.current
    if (!activePlanWait) return

    clearActivePlanWait()
    activePlanWait.reject?.(error)
  }, [clearActivePlanWait])

  const refreshBillingStatus = useCallback(async () => {
    const { getBillingStatus } = await import('../lib/billingApi')
    const status = await getBillingStatus()
    setBillingPlan((status.plan || 'free') as 'free' | 'pro2' | 'pro5')
    setBillingStatus(status)
    analytics.setUserProperties({
      plan: status.plan || 'free',
      plan_status: status.plan_status,
    })
    return status
  }, [])

  const setActivePlanTimeout = useCallback((requestId: number, timeoutId: ReturnType<typeof setTimeout>) => {
    if (activePlanWaitRef.current?.requestId !== requestId) {
      clearTimeout(timeoutId)
      return
    }
    activePlanWaitRef.current.timeoutId = timeoutId
  }, [])

  const setActivePlanEventSource = useCallback((requestId: number, eventSource: EventSource | null) => {
    if (activePlanWaitRef.current?.requestId !== requestId) {
      eventSource?.close()
      return
    }
    activePlanWaitRef.current.eventSource = eventSource
  }, [])

  const clearRealtimeResearchWork = useCallback(() => {
    abortActivePlanWait()
    pollingIntervals.current.forEach(interval => clearInterval(interval))
    pollingIntervals.current.clear()
    wsUnsubscribers.current.forEach(unsubscribe => unsubscribe())
    wsUnsubscribers.current.clear()
  }, [abortActivePlanWait])
  
  const enqueueToast = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const fire = () => onShowToast(toast)
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(fire)
    } else {
      setTimeout(fire, 0)
    }
  }, [onShowToast])
  
  const focusPendingTab = useCallback(() => {
    setStatusFilter('in_progress')
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem('insightIntelligenceRuns', JSON.stringify(intelligenceRuns))
    } catch (error) {
      console.warn('Unable to persist intelligence runs', error)
    }
  }, [intelligenceRuns])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handlePendingTabSignal = () => {
      focusPendingTab()
    }
    window.addEventListener('research:showPending', handlePendingTabSignal)
    return () => window.removeEventListener('research:showPending', handlePendingTabSignal)
  }, [focusPendingTab])

  useEffect(() => {
    onArticleOpenChange?.(showCockpit || showLegacyViewer || showProgressOverlay)
  }, [showCockpit, showLegacyViewer, showProgressOverlay, onArticleOpenChange])

  useEffect(() => {
    const onCloseOverlay = () => {
      if (showCockpit) {
        setShowCockpit(false)
        setIntelligenceReportData(null)
        setSelectedStudy(null)
      }
      if (showLegacyViewer) {
        setShowLegacyViewer(false)
        setSelectedStudy(null)
      }
      if (showProgressOverlay) {
        setShowProgressOverlay(false)
        setSelectedStudy(null)
        setIsCancellingProgressStudy(false)
      }
      onArticleOpenChange?.(false)
    }
    window.addEventListener('insightatlas-close-article', onCloseOverlay as any)
    const onBrowserPop = () => onCloseOverlay()
    window.addEventListener('popstate', onBrowserPop)
    return () => {
      window.removeEventListener('insightatlas-close-article', onCloseOverlay as any)
      window.removeEventListener('popstate', onBrowserPop)
    }
  }, [showCockpit, showLegacyViewer, showProgressOverlay, onArticleOpenChange])
  const pollingIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const wsUnsubscribers = useRef<Map<string, () => void>>(new Map())
  const [wsConnected, setWsConnected] = useState(false)
  const [showResearchModal, setShowResearchModal] = useState(false)
  const [isLoadingStudies, setIsLoadingStudies] = useState(true)
  const { user } = useAuth()
  const [researchTopic, setResearchTopic] = useState('')
  const [isDesktopPublic, setIsDesktopPublic] = useState(false)
  const [planLoadingQuery, setPlanLoadingQuery] = useState<string | null>(null)
  
  // CRITICAL SECURITY: Track user ID to prevent showing wrong user's data
  const currentUserIdRef = useRef<number | null>(null)
  const tabFetchedRef = useRef<Set<ResearchPageTab>>(new Set())
  const activePlanWaitRef = useRef<ActivePlanWait | null>(null)
  const activePlanStartRef = useRef<PendingPlanStart | null>(null)
  const planViewStartRef = useRef<number>(0)
  const planWaitCounterRef = useRef(0)
  const planStartCounterRef = useRef(0)
  const mobileAutoStartKeyRef = useRef<string | null>(null)

  // Add debug logging and scroll to top on mount
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0)
    
    // console.log('ResearchPageContent mounted, user:', user)
    if (debugMode) {
      // console.log('Debug mode enabled. Studies:', studies)
    }
    // Add specific account debugging
    if (user?.email === 'iabusam@gmail.com') {
      // console.log('Special debugging for iabusam@gmail.com account')
      // console.log('User object:', user)
      // console.log('Studies state:', studies)
    }
  }, [])

  useEffect(() => {
    try {
      localStorage.removeItem('researchStudies')
      const keysToCheck = Object.keys(localStorage)
      keysToCheck.forEach(key => {
        if (key.includes('research') || key.includes('study') || key.includes('article')) {
          try {
            const value = localStorage.getItem(key)
            if (value && value.length > 100000) {
              localStorage.removeItem(key)
            }
          } catch {
            // Ignore cleanup errors for individual keys.
          }
        }
      })
    } catch {
      // Ignore cleanup errors.
    }

    checkLocalStorageUsage()
  }, [])

  // CRITICAL SECURITY FIX: Clear studies when user changes to prevent data leak
  useEffect(() => {
    if (user && !isLogoutInProgress()) {
      return
    }
    clearRealtimeResearchWork()
  }, [clearRealtimeResearchWork, user])

  useEffect(() => {
    const newUserId = user?.id ?? null
    const previousUserId = currentUserIdRef.current
    
    // Always update the ref
    currentUserIdRef.current = newUserId

    // Always evict all user-research cache on every user resolve so stale
    // all-bucket entries from previous sessions never corrupt tab counts.
    // The initial load always uses a cacheBuster, so clearing here costs no
    // extra API requests.
    if (newUserId) {
      tabFetchedRef.current = new Set()
      try {
        apiCache.clear()
        Object.keys(localStorage)
          .filter(k => k.startsWith('cache_user_research_') || k.startsWith('cache_research_'))
          .forEach(k => localStorage.removeItem(k))
      } catch { /* storage unavailable */ }
    }
    
    // If user changed, clear all state immediately
    if (previousUserId !== newUserId) {
      console.log(`[Security] User changed from ${previousUserId} to ${newUserId}, clearing all research state`)
      setSelectedStudy(null)
      setShowCockpit(false)
      setIntelligenceReportData(null)
      setPendingPlan(null)
      setIsStartingResearch(false)
      setPlanLoadingQuery(null)
      setRecoverablePendingPlans([])
      setRecoveringPendingPlanId(null)
      setStatusCounts(DEFAULT_RESEARCH_COUNTS)
      setHasMoreStudies(false)
      setLoadedServerItemCount(0)
      setIsLoadingMoreStudies(false)
      clearActivePlanWait()

      // Clear any polling intervals
      pollingIntervals.current.forEach(interval => clearInterval(interval))
      pollingIntervals.current.clear()
      
      // Clear WebSocket subscriptions
      wsUnsubscribers.current.forEach(unsubscribe => unsubscribe())
      wsUnsubscribers.current.clear()
    }
    
    // If no user, ensure studies are empty
    if (!user) {
      setStudies([])
      setRecoverablePendingPlans([])
      setStatusCounts(DEFAULT_RESEARCH_COUNTS)
      setHasMoreStudies(false)
      setLoadedServerItemCount(0)
      setIsLoadingStudies(false)
    }
  }, [clearActivePlanWait, user?.id]) // Trigger when user ID changes

  useEffect(() => () => clearActivePlanWait(), [clearActivePlanWait])

  // Check API health on mount (defer WebSocket until actually needed)
  useEffect(() => {
    const checkHealth = async () => {
      try {
        await checkApiHealth()
        setApiHealthy(true)
      } catch (error) {
        setApiHealthy(false)
        enqueueToast({
          type: 'error',
          title: 'API Connection Failed',
          description: 'Cannot connect to research backend. Please ensure the backend server is running.'
        })
      }
    }
    checkHealth()
  }, [enqueueToast])

  // Function to clean markdown from text
  const cleanMarkdownText = (text: string): string => {
    if (!text) return ''
    
    // Remove markdown formatting
    return text
      // Remove bold/italic markers
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      // Remove headers
      .replace(/^#+\s+/gm, '')
      // Remove links but keep text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove inline code
      .replace(/`([^`]+)`/g, '$1')
      // Remove blockquotes
      .replace(/^>\s+/gm, '')
      // Clean up extra whitespace
      .replace(/\s+/g, ' ')
      .trim()
  }

  // Function to get a valid summary (not empty, not the query)
  const getValidSummary = (study: ResearchStudy): string | null => {
    const rawSummary = study.summary || study.results?.summary || ''
    if (!rawSummary) return null
    
    // Clean the summary
    const cleaned = cleanMarkdownText(
      rawSummary
        .replace(/^\*\*Executive Summary\*\*:?:?\s*/i, '')
        .replace(/^Executive Summary:?:?\s*/i, '')
        .replace(/\*\s*Key\s*findings?\s*:?.*$/i, '')
    )
    
    if (!cleaned) return null
    
    // Check if summary is just the query (not a real summary)
    const queryNormalized = (study.query || '').trim().toLowerCase()
    const summaryNormalized = cleaned.trim().toLowerCase()
    if (queryNormalized && summaryNormalized === queryNormalized) {
      return null
    }
    
    return cleaned
  }

  const normalizeStatusCounts = useCallback((counts?: Partial<ResearchListCounts>): ResearchListCounts => ({
    completed: Number(counts?.completed ?? 0),
    in_progress: Number(counts?.in_progress ?? 0),
    drafts: Number(counts?.drafts ?? 0),
  }), [])

  const dedupeStudiesByResearchId = useCallback((candidateStudies: ResearchStudy[]): ResearchStudy[] => {
    const seen = new Map<string, number>()
    const deduped: ResearchStudy[] = []

    for (const study of candidateStudies) {
      const researchKey = study.researchId?.toString() ?? study.id
      const existingIndex = seen.get(researchKey)
      if (existingIndex !== undefined) {
        const existing = deduped[existingIndex]
        if (study.isIntelligenceReport && !existing.isIntelligenceReport) {
          deduped[existingIndex] = study
        }
        continue
      }

      seen.set(researchKey, deduped.length)
      deduped.push(study)
    }

    return deduped
  }, [])

  const mergeStoredActiveRuns = useCallback((convertedStudies: ResearchStudy[]): ResearchStudy[] => {
    if (typeof window === 'undefined') {
      return convertedStudies
    }

    const storedRuns = localStorage.getItem('insightIntelligenceRuns')
    if (!storedRuns) {
      return convertedStudies
    }

    try {
      const parsedRuns = JSON.parse(storedRuns) as Array<{ reportId: string; query: string; status: string; isPublic?: boolean; createdAt?: string }>
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000
      const activeRuns = parsedRuns.filter(run => {
        const isTerminal = run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled'
        const isPlanningPhase = run.status === 'planning' || run.status === 'awaiting_confirmation'
        const alreadyInStudies = convertedStudies.some(study => study.id === `ia-${run.reportId}` || study.task_id === run.reportId)
        // A stored run the server no longer reports as active is stale local
        // state, not truth — never re-inject anything older than 24h.
        const createdAtMs = run.createdAt ? new Date(run.createdAt).getTime() : 0
        const isFresh = createdAtMs > oneDayAgo
        return !isTerminal && !isPlanningPhase && !alreadyInStudies && isFresh
      })

      if (activeRuns.length === 0) {
        return convertedStudies
      }

      const runAsStudies: ResearchStudy[] = activeRuns.map(run => ({
        id: `ia-${run.reportId}`,
        query: run.query,
        title: run.query.substring(0, 100) + (run.query.length > 100 ? '...' : ''),
        model: 'chatgpt',
        status: 'in_progress',
        isPublic: run.isPublic ?? false,
        createdAt: run.createdAt ? new Date(run.createdAt) : new Date(),
        task_id: run.reportId,
      }))

      return dedupeStudiesByResearchId([...runAsStudies, ...convertedStudies])
    } catch (error) {
      console.warn('Failed to parse stored intelligence runs', error)
      return convertedStudies
    }
  }, [dedupeStudiesByResearchId])

  const mergePaginatedStudies = useCallback((existing: ResearchStudy[], next: ResearchStudy[]) => {
    return dedupeStudiesByResearchId([...existing, ...next])
  }, [dedupeStudiesByResearchId])

  const mergePaginatedDrafts = useCallback((
    existing: RecoverablePendingPlanSummary[],
    next: RecoverablePendingPlanSummary[],
  ) => {
    const merged = [...existing]
    next.forEach(plan => {
      if (!merged.some(existingPlan => existingPlan.reportId === plan.reportId)) {
        merged.push(plan)
      }
    })
    return merged
  }, [])

  const loadResearchBucketPage = useCallback(async ({
    bucket,
    offset = 0,
    append = false,
    cacheBuster = '',
  }: {
    bucket: ResearchPageTab
    offset?: number
    append?: boolean
    cacheBuster?: string
  }) => {
    const response = await fetchUserResearchWithFallback(RESEARCH_PAGE_SIZE, offset, cacheBuster, bucket)
    const userStudies = Array.isArray(response.results) ? response.results : []
    const nextCounts = normalizeStatusCounts(response.counts)
    // Do NOT fall back to response.total for the bucket count — total may
    // reflect a stale all-bucket response with 49+ items, not just this bucket.

    // Guarantee draft visibility: if any item in the payload is a draft
    // (planning/awaiting_confirmation), surface at least that many drafts in the
    // count — regardless of what the bucket counts claim. This recovers from
    // stale caches or backend responses that zero out the draft count while
    // still returning draft-status rows.
    const draftItemsInResults = userStudies.filter(
      (s: any) => s?.status === 'planning' || s?.status === 'awaiting_confirmation'
    )
    if (draftItemsInResults.length > 0) {
      nextCounts.drafts = Math.max(nextCounts.drafts, draftItemsInResults.length)
    }

    // Merge counts so concurrent bucket fetches don't overwrite each other.
    // Always preserve the highest draft count seen across parallel responses.
    setStatusCounts(prev => ({
      ...prev,
      ...nextCounts,
      drafts: Math.max(prev.drafts, nextCounts.drafts),
    }))
    setHasMoreStudies(Boolean(response.has_more))
    setLoadedServerItemCount(offset + userStudies.length)

    if (bucket === 'drafts') {
      const nextDrafts = collectRecoverablePendingPlans(userStudies)
      setRecoverablePendingPlans(current => (
        append ? mergePaginatedDrafts(current, nextDrafts) : nextDrafts
      ))
      if (!append) {
        setStudies([])
      }
      return
    }

    // If any draft-status items leaked into a non-draft bucket response, seed
    // recoverablePendingPlans so the Drafts tab has content to show immediately
    // without requiring a separate fetch.
    const leakedDraftStudies = userStudies.filter(
      (s: any) => s?.status === 'planning' || s?.status === 'awaiting_confirmation'
    )
    if (leakedDraftStudies.length > 0 && !append) {
      const seededDrafts = collectRecoverablePendingPlans(leakedDraftStudies)
      if (seededDrafts.length > 0) {
        setRecoverablePendingPlans(seededDrafts)
      }
    }

    const convertedStudies = convertStudiesArray(userStudies, offset)
    const nextStudies = bucket === 'in_progress'
      ? mergeStoredActiveRuns(convertedStudies)
      : convertedStudies

    setStudies(current => (
      append ? mergePaginatedStudies(current, nextStudies) : nextStudies
    ))
    if (!append && leakedDraftStudies.length === 0) {
      setRecoverablePendingPlans([])
    }
  }, [
    mergePaginatedDrafts,
    mergePaginatedStudies,
    mergeStoredActiveRuns,
    normalizeStatusCounts,
  ])

  const refreshActiveResearchBucket = useCallback(() => {
    if (!user || isLogoutInProgress()) {
      return
    }

    void loadResearchBucketPage({
      bucket: statusFilter,
      offset: 0,
      append: false,
      cacheBuster: `${Date.now()}`,
    })
  }, [loadResearchBucketPage, statusFilter, user])

  // Background refresh every 5 minutes
  useEffect(() => {
    if (!user || isLogoutInProgress()) return

    const interval = setInterval(() => {
      const currentUser = currentUserIdRef.current
      if (!currentUser || currentUser !== user.id) {
        console.log('[Security] Background refresh skipped - user changed')
        return
      }

      setIsLoadingStudies(true)
      ;(async () => {
        try {
          if (isLogoutInProgress()) return
          await loadResearchBucketPage({
            bucket: statusFilter,
            offset: 0,
            append: false,
            cacheBuster: `${Date.now()}`,
          })

          if (currentUserIdRef.current !== currentUser && !isLogoutInProgress()) {
            console.log('[Security] Background refresh discarded - user changed during fetch')
          }
        } catch {
          // Swallow background refresh errors.
        } finally {
          setIsLoadingStudies(false)
        }
      })()
    }, 300000)

    return () => clearInterval(interval)
  }, [loadResearchBucketPage, statusFilter, user])

  useEffect(() => {
    let isMounted = true
    const effectUserId = user?.id ?? null

    const loadStudies = async () => {
      if (!user || !isMounted || isLogoutInProgress()) {
        if (isMounted) {
          setIsLoadingStudies(false)
        }
        return
      }

      if (currentUserIdRef.current !== effectUserId) {
        console.log(`[Security] User changed during load setup, aborting load for user ${effectUserId}`)
        return
      }

      try {
        setIsLoadingStudies(true)
        const isFirstLoad = !tabFetchedRef.current.has(statusFilter)
        tabFetchedRef.current.add(statusFilter)
        const mainFetch = loadResearchBucketPage({
          bucket: statusFilter,
          offset: 0,
          append: false,
          cacheBuster: isFirstLoad ? `${Date.now()}` : '',
        })

        // Always probe the drafts bucket in parallel so statusCounts.drafts is
        // authoritative from server truth — independent of which tab is active
        // and regardless of any stale cache entries. This is count-only: it
        // updates statusCounts.drafts without touching studies, hasMoreStudies,
        // or recoverablePendingPlans (those are set when the user navigates to
        // the Drafts tab and loadResearchBucketPage fires properly).
        if (statusFilter !== 'drafts' && !isLogoutInProgress()) {
          void (async () => {
            try {
              const draftResp = await fetchUserResearchWithFallback(1, 0, `${Date.now()}`, 'drafts')
              if (!isMounted || isLogoutInProgress()) return
              const draftsInResp = Array.isArray(draftResp.results) ? draftResp.results : []
              const draftCount = draftResp.counts?.drafts
                ?? draftsInResp.filter(
                    (s: any) => s?.status === 'planning' || s?.status === 'awaiting_confirmation'
                  ).length
              if (draftCount > 0) {
                setStatusCounts(prev => ({
                  ...prev,
                  drafts: Math.max(prev.drafts, draftCount),
                }))
              }
            } catch {
              // Draft count probe failed — not fatal.
            }
          })()
        }

        await mainFetch

        if (!isMounted || currentUserIdRef.current !== effectUserId) {
          console.log('[Security] Final user check failed, discarding loaded studies')
        }
      } catch (error) {
        console.error('Error fetching user studies:', error)
        if (isMounted && currentUserIdRef.current === effectUserId) {
          enqueueToast({
            type: 'error',
            title: 'Failed to load research studies',
            description: 'Could not load your research studies. Please try again later.'
          })
        }
      } finally {
        if (isMounted) {
          setIsLoadingStudies(false)
        }
      }
    }

    if (user) {
      const timeoutId = setTimeout(() => {
        void loadStudies()
      }, 100)
      return () => {
        isMounted = false
        clearTimeout(timeoutId)
      }
    }

    setStudies([])
    setRecoverablePendingPlans([])
    setRecoveringPendingPlanId(null)
    setStatusCounts(DEFAULT_RESEARCH_COUNTS)
    setHasMoreStudies(false)
    setLoadedServerItemCount(0)
    setIsLoadingStudies(false)
    return () => {
      isMounted = false
    }
  }, [enqueueToast, loadResearchBucketPage, statusFilter, user?.id])

  // Separate effect for monitoring incomplete studies
  useEffect(() => {
    // Note: We have both local polling (for UI updates) and global polling (continues when navigating away)
    // The global polling in ResearchPollingContext ensures notifications even when user is on other pages
    // This local polling ensures immediate UI updates when user is on the Research page
    
    // Only start monitoring if we have studies and are logged in
    if (studies.length > 0 && user && !isLogoutInProgress()) {
      // console.log('Checking studies for monitoring:', studies)
      studies.forEach((study: ResearchStudy) => {
        // console.log(`Study ${study.id} status: ${study.status}, task_id: ${study.task_id}`)
        
        // SKIP intelligence reports - they have their own polling via intelligenceRuns state
        // Intelligence reports have task_ids starting with "report_" or IDs starting with "ia-"
        // They use fetchIntelligenceStatus() instead of getResearchStatus()
        if (study.isIntelligenceReport || study.task_id?.startsWith('report_') || study.id?.toString().startsWith('ia-')) {
          // console.log(`Skipping intelligence report ${study.id} - uses separate polling`)
          return
        }
        
        if (study.task_id && (study.status === 'pending' || study.status === 'queued' || study.status === 'in_progress' || study.status === 'interrupted')) {
          // Always start polling as primary mechanism (for legacy OpenAI research only)
          if (!pollingIntervals.current.has(study.task_id)) {
            // console.log('Starting polling for task:', study.task_id)
            startPolling(study.task_id)
          } else {
            // console.log('Already polling task:', study.task_id)
          }
          
          // Also subscribe via WebSocket (manager handles connecting lazily)
          if (!wsUnsubscribers.current.has(study.task_id)) {
            // console.log('Also subscribing via WebSocket for task:', study.task_id)
            subscribeToTask(study.task_id)
          }
        } else {
          // console.log(`Not monitoring study ${study.id}: status=${study.status}, task_id=${study.task_id}`)
        }
      })
    }
    
    // Cleanup function to stop polling for completed/failed tasks
    return () => {
      studies.forEach((study) => {
        if (study.task_id && (study.status === 'completed' || study.status === 'failed')) {
          stopPolling(study.task_id)
          const unsubscribe = wsUnsubscribers.current.get(study.task_id)
          if (unsubscribe) {
            unsubscribe()
            wsUnsubscribers.current.delete(study.task_id)
          }
        }
      })
    }
  }, [studies, wsConnected, user]) // Monitor when studies, WebSocket connection, or user changes

  // Use a ref to track current runs without triggering re-renders
  const intelligenceRunsRef = useRef(intelligenceRuns)
  useEffect(() => {
    intelligenceRunsRef.current = intelligenceRuns
  }, [intelligenceRuns])

  useEffect(() => {
    const activeRuns = studies.reduce<IntelligenceRunSummary[]>((runs, study) => {
      if (!isActiveIntelligenceStudy(study)) {
        return runs
      }

      const reportId = study.intelligenceReportId || study.task_id || normalizeIntelligenceReportId(study.id)
      if (!reportId) {
        return runs
      }

      runs.push({
        reportId,
        planSummary: study.summary || '',
        personaMode: '',
        status: study.status,
        shipScore: undefined,
        query: study.query,
        createdAt: study.createdAt?.toISOString?.() ?? undefined,
      })

      return runs
    }, [])

    if (activeRuns.length === 0) {
      return
    }

    setIntelligenceRuns((prev) => {
      const merged = [...prev]
      activeRuns.forEach((run) => {
        const existingIndex = merged.findIndex((entry) => entry.reportId === run.reportId)
        if (existingIndex === -1) {
          merged.push(run)
          return
        }

        merged[existingIndex] = {
          ...merged[existingIndex],
          status: run.status,
          query: run.query,
          createdAt: run.createdAt ?? merged[existingIndex].createdAt,
        }
      })
      return merged
    })
  }, [studies])

  // Compute active report IDs to use as stable dependency
  // Only poll runs that are actively executing (not planning or terminal states)
  const activeReportIds = useMemo(() => {
    const TERMINAL_OR_WAITING_STATUSES = ['completed', 'failed', 'cancelled', 'planning', 'awaiting_confirmation']
    return intelligenceRuns
      .filter(run => !TERMINAL_OR_WAITING_STATUSES.includes(run.status))
      .map(run => run.reportId)
      .sort()
      .join(',')
  }, [intelligenceRuns])

  useEffect(() => {
    if (!activeReportIds || !user || isLogoutInProgress()) return

    let cancelled = false
    const pollStatuses = async () => {
      if (cancelled || isLogoutInProgress()) return
      const TERMINAL_OR_WAITING_STATUSES = ['completed', 'failed', 'cancelled', 'planning', 'awaiting_confirmation']
      const currentRuns = intelligenceRunsRef.current.filter(
        run => !TERMINAL_OR_WAITING_STATUSES.includes(run.status)
      )
      for (const run of currentRuns) {
        if (cancelled || isLogoutInProgress()) return
        try {
          const status = await fetchIntelligenceStatus(run.reportId)
          if (cancelled || isLogoutInProgress()) return
          
          const previousStatus = run.status
          const newStatus = status.status
          const studyStatus = (newStatus === 'completed')
            ? 'completed' as const
            : (newStatus === 'failed' || newStatus === 'cancelled')
              ? 'failed' as const
              : (newStatus === 'podcast_pending')
                ? 'podcast_pending' as const
                : (newStatus === 'interrupted')
                  ? 'interrupted' as const
                  : (newStatus === 'in_progress' || newStatus === 'collecting' || newStatus === 'processing')
                    ? 'in_progress' as const
                    : 'pending' as const
          
          // Update intelligenceRuns state
          setIntelligenceRuns(prev =>
            prev.map(entry =>
              entry.reportId === run.reportId
                ? {
                    ...entry,
                    status: newStatus,
                    shipScore: status.ship_score ?? entry.shipScore,
                  }
                : entry,
            ),
          )

          let matchedStudyQuery = run.query
          setStudies(prev => prev.map(study => {
            const isMatchingStudy = study.task_id === run.reportId ||
              study.id === `ia-${run.reportId}` ||
              (typeof study.id === 'string' && study.id.includes(run.reportId))

            if (!isMatchingStudy) {
              return study
            }

            matchedStudyQuery = study.query || run.query
            return {
              ...study,
              status: studyStatus,
              currentPhase: status.current_phase ?? study.currentPhase,
              etaSeconds: status.eta_seconds ?? study.etaSeconds,
              depth: status.depth ?? study.depth,
            }
          }))

          setSelectedStudy(prev => {
            if (!prev) return prev

            const isMatchingStudy = prev.task_id === run.reportId ||
              prev.id === `ia-${run.reportId}` ||
              (typeof prev.id === 'string' && prev.id.includes(run.reportId))

            if (!isMatchingStudy) {
              return prev
            }

            return {
              ...prev,
              status: studyStatus,
              currentPhase: status.current_phase ?? prev.currentPhase,
              etaSeconds: status.eta_seconds ?? prev.etaSeconds,
              depth: status.depth ?? prev.depth,
            }
          })

          if (newStatus !== previousStatus) {
            if (newStatus === 'completed' && previousStatus !== 'completed') {
              setTimeout(() => {
                enqueueToast({
                  type: 'success',
                  title: 'Research Completed',
                  description: `Research "${matchedStudyQuery}" has finished and results are now available.`
                })
              }, 0)
            } else if (newStatus === 'failed' && previousStatus !== 'failed') {
              setTimeout(() => {
                enqueueToast({
                  type: 'error',
                  title: 'Research Failed',
                  description: `Research "${matchedStudyQuery}" failed.`
                })
              }, 0)
            }

            if (newStatus === 'completed' || newStatus === 'failed' || newStatus === 'cancelled') {
              refreshActiveResearchBucket()
            }
          }
        } catch (error) {
          console.warn('Unable to fetch intelligence run status', error)
        }
      }
    }

    pollStatuses()
    const interval = setInterval(pollStatuses, 5000) // Poll every 5 seconds for responsive updates
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [activeReportIds, refreshActiveResearchBucket, user?.id])

  // Removed localStorage saving to prevent quota exceeded errors
  // Studies are already persisted in the backend database

  // Cleanup polling intervals and WebSocket subscriptions on unmount
  useEffect(() => {
    return () => {
      clearRealtimeResearchWork()
    }
  }, [clearRealtimeResearchWork])

  // WebSocket subscription for real-time updates
  const subscribeToTask = (taskId: string) => {
    if (wsUnsubscribers.current.has(taskId)) return

    const wsManager = getWebSocketManager()
    const unsubscribe = wsManager.subscribeToTask(taskId, (result) => {
      // console.log('📨 Received WebSocket update for task:', taskId, result)
      
      setStudies(prev => prev.map(study => {
        if (study.task_id === taskId) {
          const updatedStudy = {
            ...study,
            status: mapApiStatusToStudyStatus(result.status),
            completedAt: result.status === 'completed' ? new Date() : study.completedAt,
            isPublic: result.is_public // Update isPublic from WebSocket
          }

          // If completed, update with results
          if (result.status === 'completed' && result.report_text) {
            updatedStudy.results = {
              title: extractTitleFromMarkdown(result.report_text),
              summary: result.summary || '',
              content: result.report_text,
              keyFindings: result.key_findings || [],
              implications: [], // Could be extracted from report_text if needed
              sources: result.sources || [],
              report_text: result.report_text
            }

            // Track research completion with Amplitude
            const durationMinutes = study.createdAt 
              ? Math.floor((Date.now() - new Date(study.createdAt).getTime()) / 1000 / 60)
              : 0
            const canonicalReportId = getCanonicalReportId(study)
            analytics.trackResearchCompleted({
              research_id: study.task_id || study.id,
              report_id: canonicalReportId,
              duration_minutes: durationMinutes,
              word_count: result.report_text?.split(/\s+/).length || 0,
              has_image: !!study.image_url,
              category: study.category,
            })

            // Defer auto-save and toast to avoid state update during render
            setTimeout(() => {
              autoSaveCompletedResearch(study.id, updatedStudy)

              enqueueToast({
                type: 'success',
                title: 'Research Completed',
                description: `Research "${study.query}" has finished and results are now available.`
              })
            }, 0)
          } else if (result.status === 'failed') {
            const canonicalReportId = getCanonicalReportId(study)
            analytics.trackResearchFailed({
              research_id: study.task_id || study.id,
              report_id: canonicalReportId,
              error_type: 'research_failed',
              error_message: result.error_details || 'Unknown error',
              query_length: study.query?.length,
            })
            enqueueToast({
              type: 'error',
              title: 'Research Failed',
              description: `Research "${study.query}" failed: ${result.error_details || 'Unknown error'}`
            })
          }

          return updatedStudy
        }
        return study
      }))

      if (result.status === 'completed' || result.status === 'failed' || result.status === 'cancelled') {
        refreshActiveResearchBucket()
      }
    })

    wsUnsubscribers.current.set(taskId, unsubscribe)
  }

  const startPolling = (taskId: string) => {
    if (pollingIntervals.current.has(taskId)) return

    // console.log(`🔄 Starting polling for task ${taskId}`)
    
    // Poll immediately on start
    const pollOnce = async () => {
      if (!currentUserIdRef.current || isLogoutInProgress()) {
        stopPolling(taskId)
        return
      }
      try {
        const status = await getResearchStatus(taskId)
        // console.log(`📊 Polling update for ${taskId}:`, status)
        
        setStudies(prev => prev.map(study => {
          if (study.task_id === taskId) {
            const previousStatus = study.status
            const updatedStudy = {
              ...study,
              status: mapApiStatusToStudyStatus(status.status),
              progress: status.progress || study.progress,
              currentPhase: status.current_phase || study.currentPhase,  // Real phase from backend
              isPublic: status.is_public // Update isPublic from polling
            }

            // If status changed from in_progress to completed
            if ((previousStatus === 'in_progress' || previousStatus === 'pending' || previousStatus === 'queued' || previousStatus === 'interrupted' || previousStatus === 'podcast_pending') && 
                status.status === 'completed') {
              // Stop polling for this task
              stopPolling(taskId)
              refreshActiveResearchBucket()
              
              // Fetch the full results
              getResearchResult(taskId).then(result => {
                setStudies(prev => prev.map(s => {
                  if (s.task_id === taskId) {
                    const completedStudy = {
                      ...s,
                      status: 'completed' as const,
                      completedAt: new Date(),
                      results: {
                        title: extractTitleFromMarkdown(result.report_text || ''),
                        summary: result.summary || '',
                        content: result.report_text || 'No content available',
                        keyFindings: result.key_findings || [],
                        implications: [],
                        sources: result.sources || [],
                        report_text: result.report_text || ''
                      },
                      isPublic: result.is_public // Update isPublic from results
                    }
                    
                    // Defer auto-save and toast to avoid state update during render
                    setTimeout(() => {
                      const durationMinutes = s.createdAt
                        ? Math.floor((Date.now() - new Date(s.createdAt).getTime()) / 1000 / 60)
                        : 0
                      const canonicalReportId = getCanonicalReportId(s)

                      analytics.trackResearchCompleted({
                        research_id: s.task_id || s.id,
                        report_id: canonicalReportId,
                        duration_minutes: durationMinutes,
                        word_count: result.report_text?.split(/\s+/).length || 0,
                        has_image: !!s.image_url,
                        category: s.category,
                      })

                      autoSaveCompletedResearch(s.id, completedStudy)
                      
                      // Show toast notification
                      enqueueToast({
                        type: 'success',
                        title: 'Research Completed',
                        description: `Research "${s.query}" has finished and results are now available.`
                      })
                    }, 0)
                    
                    return completedStudy
                  }
                  return s
                }))
              }).catch(error => {
                console.error('Error fetching results:', error)
                enqueueToast({
                  type: 'error',
                  title: 'Error',
                  description: 'Failed to fetch research results'
                })
              })
            } else if (status.status === 'failed' || status.status === 'cancelled') {
              // Stop polling for failed/cancelled tasks
              stopPolling(taskId)
              refreshActiveResearchBucket()
              
              // Only show toast for failed (not cancelled - user initiated that)
              if (status.status === 'failed') {
                const canonicalReportId = getCanonicalReportId(study)
                analytics.trackResearchFailed({
                  research_id: study.task_id || study.id,
                  report_id: canonicalReportId,
                  error_type: 'research_failed',
                  error_message: status.error_details || 'Unknown error',
                  query_length: study.query?.length,
                })
                enqueueToast({
                  type: 'error',
                  title: 'Research Failed',
                  description: `Research "${study.query}" failed: ${status.error_details || 'Unknown error'}`
                })
              }
            }

            return updatedStudy
          }
          return study
        }))
      } catch (error) {
        console.error(`Error polling status for ${taskId}:`, error)
        // Don't stop polling on error - might be temporary network issue
      }
    }
    
    // Poll immediately
    pollOnce()
    
    // Then set up interval
    const interval = setInterval(pollOnce, 5000) // Poll every 5 seconds
    pollingIntervals.current.set(taskId, interval)
  }

  // Manual retry for failed research
  // Supports both intelligence reports (string ID) and legacy research (numeric ID)
  const retryFailedResearch = async (study: ResearchStudy) => {
    try {
      let res: Response
      const normalizedReportId = study.intelligenceReportId || normalizeIntelligenceReportId(study.id)
      const isIntelligenceReport = study.isIntelligenceReport && normalizedReportId
      
      // Intelligence reports use the intelligence endpoint with string report_id
      if (isIntelligenceReport) {
        res = await fetch(`${API_BASE_URL}/api/intelligence/research/${normalizedReportId}/retry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() }
        })
      } else if (study.researchId) {
        // Legacy research uses the admin endpoint with numeric ID
        res = await fetch(`${API_BASE_URL}/admin/research/${study.researchId}/retry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() }
        })
      } else {
        enqueueToast({ type: 'error', title: 'Retry Failed', description: 'No valid research ID found.' })
        return
      }
      
      const data = await res.json().catch(() => ({}))
      if (res.ok && (data.new_task_id || data.report_id || data.success)) {
        enqueueToast({ type: 'success', title: 'Retry Submitted', description: 'We will notify you when it completes.' })
        analytics.trackResearchRetried({
          research_id: normalizedReportId || study.task_id || study.id,
          source: isIntelligenceReport ? 'intelligence_report' : 'legacy_research',
        })
        
        // Immediately reflect in UI: set status to in_progress
        setStudies(prev => prev.map(s => s.id === study.id ? {
          ...s,
          status: 'in_progress',
          task_id: data.new_task_id || data.report_id || s.task_id,
          progress: 0,
          currentPhase: 'COLLECT' // Reset phase for retry
        } : s))
        
        // For intelligence reports, add to intelligenceRuns so the correct polling loop picks it up
        // This uses fetchIntelligenceStatus() instead of getResearchStatus()
        if (isIntelligenceReport && normalizedReportId) {
          setIntelligenceRuns(prev => {
            // Check if already exists
            const exists = prev.some(run => run.reportId === normalizedReportId)
            if (exists) {
              // Update existing run status
              return prev.map(run => 
                run.reportId === normalizedReportId 
                  ? { ...run, status: 'queued', shipScore: undefined }
                  : run
              )
            } else {
              // Add new run entry for polling
              const newRun: IntelligenceRunSummary = {
                reportId: normalizedReportId,
                query: study.query,
                planSummary: '',
                personaMode: '',
                status: 'queued',
                createdAt: new Date().toISOString(),
                shipScore: undefined
              }
              return [...prev, newRun]
            }
          })
        } else {
          // For legacy research, use the old polling mechanism
          const newTaskId = data.new_task_id || data.report_id
          if (newTaskId) {
            startPolling(newTaskId)
          }
        }
      } else {
        enqueueToast({ type: 'error', title: 'Retry Failed', description: data.detail || data.message || 'Unable to retry.' })
      }
    } catch (e) {
      enqueueToast({ type: 'error', title: 'Retry Error', description: 'Network error while retrying.' })
    }
  }

  const stopPolling = (taskId: string) => {
    const interval = pollingIntervals.current.get(taskId)
    if (interval) {
      clearInterval(interval)
      pollingIntervals.current.delete(taskId)
    }
  }



  const mapApiStatusToStudyStatus = (apiStatus: string): ResearchStudy['status'] => {
    switch (apiStatus) {
      case 'pending':
      case 'queued':
      case 'planning':
      case 'awaiting_confirmation':
        return 'pending'
      case 'in_progress':
      case 'running':
        return 'in_progress'
      case 'interrupted':
        return 'interrupted'
      case 'podcast_pending':
        return 'podcast_pending'
      case 'completed':
        return 'completed'
      case 'failed':
      case 'cancelled':
        return 'failed'
      default:
        return 'pending'
    }
  }

  const isPlanOnlyIntelligenceStudy = (study: Pick<ResearchStudy, 'status' | 'currentPhase' | 'isIntelligenceReport' | 'results' | 'completedAt'>) => {
    if (!study.isIntelligenceReport) return false
    if (study.completedAt || study.results?.report_text) return false
    const status = study.status as string
    return status === 'planning' || status === 'awaiting_confirmation'
  }

  const isActiveIntelligenceStudy = (
    study: Pick<ResearchStudy, 'status' | 'currentPhase' | 'isIntelligenceReport' | 'results' | 'completedAt'>
  ) => (
    study.isIntelligenceReport === true &&
    ACTIVE_INTELLIGENCE_STATUSES.includes(study.status) &&
    !isPlanOnlyIntelligenceStudy(study)
  )

  const isClickableStudyCard = (study: ResearchStudy) => (
    study.status === 'completed' || isActiveIntelligenceStudy(study)
  )

  const getProgressPresentation = (
    study: Pick<ResearchStudy, 'status' | 'currentPhase' | 'createdAt' | 'statusMessage'>
  ) => {
    const progressState = getIntelligenceProgressState({
      status: study.status,
      rawPhase: study.currentPhase,
      createdAt: study.createdAt,
    })
    const phase = getIntelligenceProgressPhase({
      status: study.status,
      rawPhase: study.currentPhase,
      createdAt: study.createdAt,
    })
    const label = getIntelligenceProgressLabel({
      status: study.status,
      rawPhase: study.currentPhase,
      createdAt: study.createdAt,
      stepLabel: study.statusMessage,
    })

    return {
      label,
      progress: progressState.progress,
      phaseNum: phase.phaseNumber,
    }
  }

  // Extract or generate compelling title from markdown content
  const extractTitleFromMarkdown = (markdown: string): string => {
    if (!markdown) return 'Research Report'
    
    // Look for existing title patterns
    const titleMatch = markdown.match(/^#\s+(.+)$/m)
    if (titleMatch) {
      return titleMatch[1].trim()
    }
    
    // Look for executive summary title
    const execMatch = markdown.match(/^##?\s*Executive Summary[:\s]*(.+)$/mi)
    if (execMatch) {
      return execMatch[1].trim()
    }
    
    // Extract first meaningful sentence and create title
    const firstParagraph = markdown.split('\n\n')[0]
    const cleanText = firstParagraph
      .replace(/[#*]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    
    if (cleanText.length > 10) {
      // Look for key insights to create compelling titles
      const lowerText = cleanText.toLowerCase()
      
      if (lowerText.includes('breakthrough') || lowerText.includes('revolutionary')) {
        return `Revolutionary Breakthrough: ${cleanText.slice(0, 50)}...`
      }
      if (lowerText.includes('crisis') || lowerText.includes('urgent')) {
        return `Critical Analysis: ${cleanText.slice(0, 50)}...`
      }
      if (lowerText.includes('future') || lowerText.includes('predict')) {
        return `Future Insights: ${cleanText.slice(0, 50)}...`
      }
      if (lowerText.includes('impact') || lowerText.includes('effect')) {
        return `Impact Analysis: ${cleanText.slice(0, 50)}...`
      }
      if (lowerText.includes('trend') || lowerText.includes('pattern')) {
        return `Emerging Trends: ${cleanText.slice(0, 50)}...`
      }
      if (lowerText.includes('discover') || lowerText.includes('finding')) {
        return `Key Discoveries: ${cleanText.slice(0, 50)}...`
      }
      
      // Default compelling format
      return `Research Insights: ${cleanText.slice(0, 60)}...`
    }
    
    return 'Comprehensive Research Report'
  }

const normalizeIntelligenceReportId = (value?: string | null): string | undefined => {
  if (!value) return undefined
  const str = value.toString().trim()
  if (!str) return undefined
  
  if (str.startsWith('report_') || str.startsWith('report-')) {
    return str
  }
  
  if (str.startsWith('ia-report_') || str.startsWith('ia-report-')) {
    // Strip the ia- prefix (backend endpoints expect report_xxx/report-xxx)
    return str.replace(/^ia-/, '')
  }

  const prefixedMatch = str.match(/ia-(report[-_][a-z0-9-]+)/i)
  if (prefixedMatch) {
    return prefixedMatch[1]
  }
  
  const match = str.match(/report[-_][a-z0-9-]+/i)
  return match ? match[0] : undefined
}

const getCanonicalReportId = (study: Pick<ResearchStudy, 'id' | 'task_id' | 'intelligenceReportId'>): string | undefined => {
  return study.intelligenceReportId || study.task_id || normalizeIntelligenceReportId(study.id)
}

const normalizeStudyPrivacy = (study: any): boolean => {
    const directValue = study?.is_public ?? study?.isPublic ?? study?.public
    if (typeof directValue === 'boolean') return directValue
    if (typeof directValue === 'number') return directValue === 1
    if (typeof directValue === 'string') {
      const normalized = directValue.trim().toLowerCase()
      if (['public', 'true', '1'].includes(normalized)) return true
      if (['private', 'false', '0'].includes(normalized)) return false
    }
    const privacyString = study?.privacy || study?.visibility
    if (typeof privacyString === 'string') {
      return privacyString.trim().toLowerCase() === 'public'
    }
    return false
  }

  const parseBackendDate = (value: string | number | Date | undefined): Date => {
    if (!value) return new Date()
    try {
      if (value instanceof Date) {
        return value
      }
      if (typeof value === 'number') {
        const timestamp = value > 1e12 ? value : value * 1000
        const parsed = new Date(timestamp)
        if (!isNaN(parsed.getTime())) {
          return parsed
        }
      }
      if (typeof value === 'string') {
        let dateStr = value
        if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('T')) {
          dateStr = dateStr.replace(' ', 'T') + 'Z'
        } else if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+')) {
          dateStr = `${dateStr}Z`
        }
        const parsed = new Date(dateStr)
        if (!isNaN(parsed.getTime())) {
          return parsed
        }
      }
    } catch (error) {
      console.warn('Failed to parse backend date:', value, error)
    }
    return new Date()
  }

  const convertApiStudy = (study: any, logIndex?: number): ResearchStudy | null => {
    try {
      if (!study || typeof study !== 'object') {
        console.error('Invalid study object', logIndex, study)
        return null
      }

      // IMPORTANT: Filter out studies in planning phase - these should only appear
      // in the inline plan confirmation flow, not in the Research Studies list
      if (study.status === 'planning' || study.status === 'awaiting_confirmation') {
        return null
      }

      let status: ResearchStudy['status'] = study.status as ResearchStudy['status']
      if (study.results?.report_text || study.completed_at) {
        status = 'completed'
      }

      const createdAt = parseBackendDate(study.created_at)

      const normalizedResults = study.results ? {
        title: study.title || extractTitleFromMarkdown(study.results.report_text || ''),
        summary: study.results.summary || '',
        content: study.results.report_text || 'No content available',
        keyFindings: study.results.key_findings || [],
        implications: [],
        sources: study.results.sources || [],
        report_text: study.results.report_text || ''
      } : undefined

      const normalizedReportId = normalizeIntelligenceReportId(
        study.intelligence_report_id || study.report_id || study.id?.toString()
      )

      return {
        id: study.id?.toString() ?? '',
        query: study.query,
        model: (study.model as 'chatgpt' | 'gemini' | 'claude') || 'chatgpt',
        status,
        createdAt,
        completedAt: study.completed_at ? parseBackendDate(study.completed_at) : undefined,
        task_id: study.task_id,
        image_url: study.image_url,
        etaSeconds: typeof study.eta_seconds === 'number' ? study.eta_seconds : undefined,
        statusMessage: study.status_message,
        currentPhase: study.current_phase,  // Real phase from backend
        planRetryCount: typeof study.plan_retry_count === 'number' ? study.plan_retry_count : undefined,
        planRetryMax: typeof study.plan_retry_max === 'number' ? study.plan_retry_max : undefined,
        isIntelligenceReport: study.is_intelligence_report === true || Boolean(normalizedReportId || study.id?.toString().startsWith('ia-')),
        intelligenceReportId: normalizedReportId,
        // Numeric research_id for Save/Share APIs (available for completed intelligence reports)
        researchId: typeof study.research_id === 'number' ? study.research_id : undefined,
        title: (() => {
          const candidate = study.title || (study.results ? extractTitleFromMarkdown(study.results.report_text || '') : '')
          if (candidate && candidate.toLowerCase() !== 'executive summary') return candidate
          const h1 = (study.results?.report_text || '').match(/^#\s+(.+)$/m)?.[1]
          return h1 && h1.toLowerCase() !== 'executive summary' ? h1 : (study.query || 'Research Report')
        })(),
        summary: study.summary || normalizedResults?.summary || '',
        results: normalizedResults,
        isPublic: normalizeStudyPrivacy(study),
        author: study.author_name ? {
          name: study.author_name,
          avatar_url: study.author_avatar,
          email: study.author_email
        } : study.author ? {
          name: study.author.name,
          avatar_url: study.author.avatar_url,
          email: study.author.email
        } : undefined,
        category: study.category,
        depth: study.depth as ResearchStudy['depth'],
        podcast_status: study.podcast_status,
        podcast_audio_ready: study.podcast_audio_ready,
        podcast_duration_seconds: study.podcast_duration_seconds,
        podcast_error_message: study.podcast_error_message,
      }
    } catch (error) {
      console.error('Error converting study', logIndex, error)
      return null
    }
  }

  const convertStudiesArray = (rawStudies: any[], offset: number = 0): ResearchStudy[] => {
    if (!Array.isArray(rawStudies)) return []
    const converted = filterProductionDiscoveryItems(rawStudies)
      .map((study, index) => convertApiStudy(study, offset + index))
      .filter((study): study is ResearchStudy => Boolean(study))
    return converted
  }

  const autoSaveCompletedResearch = async (studyId: string, study: ResearchStudy) => {
    try {
      // Convert study ID to number for API call
      const researchId = parseInt(studyId)
      if (!isNaN(researchId)) {
        // The saveResearchArticle function is no longer needed here as it's handled by updateResearchPrivacy
        // await saveResearchArticle(researchId) 
        enqueueToast({
          type: 'info',
          title: 'Auto-saved',
          description: `Research "${study.query}" has been automatically saved to your collection.`
        })
      }
    } catch (error) {
      console.error('Failed to auto-save research:', error)
      // Don't show error toast for auto-save failures as it's not critical
    }
  }

  // Manual function to refresh all study statuses
  const refreshAllStudyStatuses = async () => {
    // console.log('🔄 Manually refreshing all study statuses...')
    
    for (const study of studies) {
      if (study.task_id) {
        try {
          // console.log(`Checking status for task ${study.task_id}...`)
          const status = await getResearchStatus(study.task_id)
          // console.log(`Task ${study.task_id} status:`, status)
          
          // If completed, get the results
          if (status.status === 'completed') {
            try {
              const result = await getResearchResult(study.task_id)
              // console.log(`Task ${study.task_id} results:`, result)
              
              setStudies(prev => prev.map(s => {
                if (s.task_id === study.task_id) {
                  return {
                    ...s,
                    status: 'completed' as const,
                    completedAt: new Date(),
                    results: {
                      title: extractTitleFromMarkdown(result.report_text || ''),
                      summary: result.summary || '',
                      content: result.report_text || 'No content available',
                      keyFindings: result.key_findings || [],
                      implications: [],
                      sources: result.sources || [],
                      report_text: result.report_text || ''
                    },
                    isPublic: result.is_public // Update isPublic from results
                  }
                }
                return s
              }))
            } catch (resultError) {
              console.error(`Error fetching results for task ${study.task_id}:`, resultError)
            }
          } else {
            // Update status even if not completed
            setStudies(prev => prev.map(s => {
              if (s.task_id === study.task_id) {
                return {
                  ...s,
                  status: mapApiStatusToStudyStatus(status.status),
                  isPublic: status.is_public // Update isPublic from polling
                }
              }
              return s
            }))
          }
        } catch (error) {
          console.error(`Error checking status for task ${study.task_id}:`, error)
        }
      }
    }
  }

  // Add a button to manually refresh (for debugging)
  const handleManualRefresh = () => {
    // console.log('Manual refresh triggered')
    refreshAllStudyStatuses()
  }

  const setPendingPlanForReview = useCallback((
    result: PlanGenerationResult,
    researchQuery: string,
    isPublicResearch: boolean,
    depth: ResearchDepth = 'maximum'
  ) => {
    planViewStartRef.current = Date.now()
    analytics.track('Plan Viewed', {
      report_id: result.reportId,
      query_length: researchQuery.length,
      depth,
      is_public: isPublicResearch,
    })
    setPendingPlan({
      reportId: result.reportId,
      plan: result.plan,
      planPreview: result.planPreview,
      query: researchQuery,
      isPublic: isPublicResearch,
      depth,
    })
  }, [])

  const pollForPlan = useCallback(async (
    reportId: string,
    fallbackQuery: string,
    requestId: number,
    resolve: (value: PlanGenerationResult) => void,
    reject: (reason: Error) => void,
  ) => {
    const maxAttempts = 180  // 15 minutes at 5-second intervals
    const pollInterval = 5000  // 5 seconds
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, pollInterval))

      if (activePlanWaitRef.current?.requestId !== requestId) {
        return
      }
      
      try {
        const planResponse = await fetchIntelligencePlan(reportId)
        
        if (planResponse.plan && Object.keys(planResponse.plan).length > 0) {
          const normalizedPlan = normalizeResearchPlan(planResponse.plan, fallbackQuery)
          console.log(`[Poll] Received plan after ${attempt + 1} attempts`)
          resolve({
            reportId,
            plan: normalizedPlan,
            planPreview: normalizedPlan.plan_summary || 'Research plan ready',
          })
          return
        }
      } catch (err: unknown) {
        if (activePlanWaitRef.current?.requestId !== requestId) {
          return
        }

        if (err instanceof IntelligenceApiError) {
          if (err.status === 202 || err.status === 404) {
            console.log(`[Poll] Attempt ${attempt + 1}/${maxAttempts} - still generating...`)
            continue
          }

          reject(err)
          return
        }

        const errorMessage = err instanceof Error ? err.message : 'Failed to load research plan.'
        if (errorMessage.includes('planning')) {
          console.log(`[Poll] Attempt ${attempt + 1}/${maxAttempts} - still generating...`)
          continue
        }

        reject(new Error(errorMessage))
        return
      }
    }
    
    reject(new Error('Plan generation timed out. Please try again.'))
  }, [])

  // Shared function for desktop inline, mobile modal, and landing-page auto-start flows.
  const handleStartResearchWithResult = useCallback(async (
    researchQuery: string,
    isPublicResearch: boolean,
    depth: ResearchDepth = 'standard',
  ): Promise<PlanGenerationResult> => {
    const startRequestId = ++planStartCounterRef.current
    activePlanStartRef.current = {
      requestId: startRequestId,
      cancelled: false,
      reportId: null,
    }

    const clearActivePlanStart = () => {
      if (activePlanStartRef.current?.requestId === startRequestId) {
        activePlanStartRef.current = null
      }
    }

    const throwIfPlanStartCancelled = async (reportId?: string) => {
      if (
        activePlanStartRef.current?.requestId === startRequestId &&
        activePlanStartRef.current.cancelled
      ) {
        clearActivePlanStart()

        if (reportId) {
          try {
            await cancelIntelligenceReport(reportId)
          } catch (cancelError) {
            console.warn('Failed to cancel report on backend after local plan cancel:', cancelError)
          }
        }

        throw new Error(PLAN_WAIT_CANCELLED_MESSAGE)
      }
    }

    const trimmedQuery = researchQuery.trim()
    
    if (!trimmedQuery || trimmedQuery.length < 10) {
      clearActivePlanStart()
      throw new Error('Research query needs to be at least 10 characters long.')
    }

    if (!apiHealthy) {
      clearActivePlanStart()
      throw new Error('Backend API is not available.')
    }

    if (!user) {
      clearActivePlanStart()
      throw new Error('Please log in to start research.')
    }

    try {
      // Billing pre-check
      try {
        const status = await refreshBillingStatus()
        if (!status.can_start) {
          analytics.trackLimitReached({
            limit_type: 'report_limit',
            plan: (status.plan || 'free') as 'free' | 'pro2' | 'pro5',
            research_count_today: 0,
            research_count_month: status.monthly_report_used || 0,
            plan_status: status.plan_status,
            entrypoint: 'research_page',
          })
          setShowUpgradeModal(true)
          throw new Error('Report limit reached. Please upgrade or purchase a single report.')
        }
        setWillBillNotice(null)
      } catch (e) {
        if (e instanceof Error && e.message.includes('limit')) throw e
        // Non-fatal: proceed, backend will enforce if needed
      }

      await throwIfPlanStartCancelled()

      const topicId = slugifyTopic(trimmedQuery)
      const personaMode = inferPersonaMode(trimmedQuery)
      
      console.log(`🔬 Starting research with depth: ${depth}`)
      
      // Start the research - backend returns immediately while plan generates in background
      const response = await startIntelligenceRun({
        user_prompt: trimmedQuery,
        persona_mode: personaMode,
        topic_id: topicId,
        topic_description: trimmedQuery,
        is_public: isPublicResearch,
        depth: depth,  // Pass the selected depth to the backend
      })

      const reportId = response.report_id
      if (activePlanStartRef.current?.requestId === startRequestId) {
        activePlanStartRef.current.reportId = reportId
      }

      await throwIfPlanStartCancelled(reportId)
      
      // If plan is already populated (shouldn't happen with background generation, but handle it)
      if (response.plan && Object.keys(response.plan).length > 0) {
        clearActivePlanStart()
        return {
          reportId,
          plan: response.plan,
          planPreview: response.plan_preview,
        }
      }
      
      // Use Server-Sent Events (SSE) for real-time updates - much more efficient than polling
      return new Promise((resolve, reject) => {
        clearActivePlanStart()
        clearActivePlanWait()
        const requestId = ++planWaitCounterRef.current
        activePlanWaitRef.current = {
          requestId,
          reportId,
          eventSource: null,
          timeoutId: null,
          reject,
        }

        const finishRequest = () => {
          if (activePlanWaitRef.current?.requestId !== requestId) return
          clearActivePlanWait()
        }

        const rejectRequest = (error: Error) => {
          if (activePlanWaitRef.current?.requestId !== requestId) return
          finishRequest()
          reject(error)
        }

        const resolveRequest = (result: PlanGenerationResult) => {
          if (activePlanWaitRef.current?.requestId !== requestId) return
          finishRequest()
          resolve(result)
        }

        const token = localStorage.getItem('access_token')
        if (!token) {
          rejectRequest(new Error('Your session expired before the plan was ready. Please log in again.'))
          return
        }

        // EventSource doesn't support custom headers, so we append token as query param
        const sseUrl = `${API_BASE_URL}/api/intelligence/research/${reportId}/stream?token=${encodeURIComponent(token)}`
        
        console.log('[SSE] Connecting to plan status stream...')
        const eventSource = createIntelligenceEventSource(sseUrl)
        setActivePlanEventSource(requestId, eventSource)
        
        // Timeout after 20 minutes (xhigh reasoning can take 15+ minutes)
        const timeout = setTimeout(() => {
          console.log('[SSE] Timeout - closing connection')
          rejectRequest(new Error('Plan generation timed out. Please try again.'))
        }, 20 * 60 * 1000)
        setActivePlanTimeout(requestId, timeout)
        
        eventSource.onmessage = async (event) => {
          if (activePlanWaitRef.current?.requestId !== requestId) return

          let data: any
          try {
            data = JSON.parse(event.data)
          } catch (parseError) {
            console.log('[SSE] Parse error:', parseError)
            rejectRequest(new Error('Received an invalid plan status update. Please try again.'))
            return
          }

          try {
            console.log('[SSE] Status update:', data)
            
            // Check if plan is ready (status should be 'awaiting_confirmation')
            if (data.status === 'awaiting_confirmation') {
              console.log('[SSE] Plan ready! Fetching full plan...')
              eventSource.close()
              setActivePlanEventSource(requestId, null)
              
              // Fetch the full plan
              const planResponse = await fetchIntelligencePlan(reportId)
              const normalizedPlan = normalizeResearchPlan(planResponse.plan, researchQuery)
              
              resolveRequest({
                reportId,
                plan: normalizedPlan,
                planPreview: normalizedPlan.plan_summary || 'Research plan ready',
              })
            } else if (data.status === 'failed' || data.error) {
              console.log('[SSE] Plan generation failed:', data.error || 'Unknown error')
              rejectRequest(new Error(data.error || 'Plan generation failed. Please try again.'))
            } else if (data.stream_ended || data.final) {
              console.log('[SSE] Stream ended without plan ready')
              rejectRequest(new Error('Plan generation ended unexpectedly. Please try again.'))
            }
          } catch (planError) {
            console.log('[SSE] Plan fetch failed:', planError)
            rejectRequest(planError instanceof Error ? planError : new Error('Failed to load research plan.'))
          }
        }
        
        eventSource.onerror = (error) => {
          if (activePlanWaitRef.current?.requestId !== requestId) return
          console.log('[SSE] Connection error, falling back to polling:', error)
          eventSource.close()
          setActivePlanEventSource(requestId, null)
          
          // Fallback to polling if SSE fails
          void pollForPlan(reportId, researchQuery, requestId, resolveRequest, rejectRequest)
        }
      })
    } catch (error) {
      clearActivePlanStart()
      throw error
    }
  }, [
    apiHealthy,
    clearActivePlanWait,
    planStartCounterRef,
    pollForPlan,
    refreshBillingStatus,
    setActivePlanEventSource,
    setActivePlanTimeout,
    user,
  ])

  const handleReviewRecoverablePlan = useCallback(async (pendingReport: RecoverablePendingPlanSummary) => {
    setRecoveringPendingPlanId(pendingReport.reportId)
    try {
      const planResponse = await fetchIntelligencePlan(pendingReport.reportId)
      const normalizedPlan = normalizeResearchPlan(planResponse.plan, pendingReport.query)
      setPendingPlanForReview(
        {
          reportId: pendingReport.reportId,
          plan: normalizedPlan,
          planPreview: normalizedPlan.plan_summary || pendingReport.planPreview,
        },
        pendingReport.query,
        pendingReport.isPublic,
        'maximum',
      )
    } catch (error) {
      if (error instanceof IntelligenceApiError && (error.status === 202 || error.status === 404)) {
        enqueueToast({
          type: 'info',
          title: 'Plan Still Preparing',
          description: 'This draft plan is still being prepared. Try again in a moment or cancel it if you no longer need it.',
        })
      } else {
        const message = error instanceof Error ? error.message : 'Unable to load your pending plan.'
        enqueueToast({
          type: 'error',
          title: 'Plan Recovery Failed',
          description: message,
        })
      }
    } finally {
      setRecoveringPendingPlanId(current =>
        current === pendingReport.reportId ? null : current
      )
    }
  }, [enqueueToast, setPendingPlanForReview])

  const handleStartResearch = useCallback(async (
    researchQuery: string,
    isPublicResearch?: boolean,
    _modelType?: 'chatgpt' | 'gemini' | 'claude',
    depth: ResearchDepth = 'maximum',
  ) => {
    const trimmedQuery = researchQuery.trim()
    
    if (!trimmedQuery) {
      enqueueToast({
        type: 'error',
        title: 'Query Required',
        description: 'Please enter a research query before starting.'
      })
      return
    }

    setIsStartingResearch(true)
    setPlanLoadingQuery(trimmedQuery)
    setShowResearchModal(false)

    try {
      const result = await handleStartResearchWithResult(trimmedQuery, isPublicResearch ?? false, depth)
      setPendingPlanForReview(result, trimmedQuery, isPublicResearch ?? false, depth)
      analytics.trackPlanGenerated({
        report_id: result.reportId,
        query_length: trimmedQuery.length,
        is_public: isPublicResearch ?? false,
        depth,
        source: 'research_page',
      })

      enqueueToast({
        type: 'success',
        title: 'Plan Generated',
        description: 'Review the proposed research plan, customize it, then approve to begin.',
      })
    } catch (error) {
      if (error instanceof Error && error.message === PLAN_WAIT_CANCELLED_MESSAGE) {
        return
      }

      // Prompt-quality hard gate: open the scope modal instead of a toast so
      // users see examples and can retry with their query preserved.
      if (
        error instanceof IntelligenceApiError &&
        typeof error.detail === 'object' &&
        error.detail !== null &&
        (error.detail as { code?: string }).code === 'OUT_OF_SCOPE'
      ) {
        const detailObj = error.detail as { message?: string }
        const gateMessage =
          detailObj.message || "This topic is outside our investment research scope."
        setOutOfScope({ visible: true, message: gateMessage })
        analytics.trackError({
          error_type: 'research_gate_blocked',
          error_message: gateMessage,
          page: 'research',
        })
        return
      }

      console.error('Research submission failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to start research'

      enqueueToast({
        type: 'error',
        title: errorMessage.includes('at least 10 characters') ? 'Query Too Short' : 'Research Failed',
        description: errorMessage
      })
    } finally {
      setIsStartingResearch(false)
      setPlanLoadingQuery(null)
    }
  }, [enqueueToast, handleStartResearchWithResult, setPendingPlanForReview])

  const startPendingLandingResearch = useCallback(async (surfaceErrors: boolean = false) => {
    if (!isMobile || !pendingLandingResearch || pendingPlan || isStartingResearch) {
      return
    }

    const { query, depth, isPublic } = pendingLandingResearch
    setIsStartingResearch(true)
    setPlanLoadingQuery(query)

    try {
      const result = await handleStartResearchWithResult(query, isPublic, depth)
      setPendingPlanForReview(result, query, isPublic, depth)
      handleAutoStartConsumed()
      analytics.trackPlanGenerated({
        report_id: result.reportId,
        query_length: query.length,
        is_public: isPublic,
        depth,
        source: 'landing_handoff',
      })

      enqueueToast({
        type: 'success',
        title: 'Plan Generated',
        description: 'Review the proposed research plan, customize it, then approve to begin.',
      })
    } catch (error) {
      if (error instanceof Error && error.message === PLAN_WAIT_CANCELLED_MESSAGE) {
        return
      }

      // Prompt-quality hard gate surfaces the scope modal rather than a toast
      // even for landing-page auto-starts so users understand what we research.
      if (
        error instanceof IntelligenceApiError &&
        typeof error.detail === 'object' &&
        error.detail !== null &&
        (error.detail as { code?: string }).code === 'OUT_OF_SCOPE'
      ) {
        const detailObj = error.detail as { message?: string }
        const gateMessage =
          detailObj.message || "This topic is outside our investment research scope."
        setOutOfScope({ visible: true, message: gateMessage })
        handleAutoStartConsumed()
        analytics.trackError({
          error_type: 'research_gate_blocked',
          error_message: gateMessage,
          page: 'research',
          source: 'landing_handoff',
        })
        return
      }

      console.error('Pending landing research failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to start research'
      const shouldSuppressToast = !surfaceErrors && errorMessage.includes('Report limit reached')

      if (!shouldSuppressToast) {
        enqueueToast({
          type: 'error',
          title: 'Research Failed',
          description: errorMessage,
        })
      }
    } finally {
      setIsStartingResearch(false)
      setPlanLoadingQuery(null)
    }
  }, [
    enqueueToast,
    handleAutoStartConsumed,
    handleStartResearchWithResult,
    isMobile,
    isStartingResearch,
    pendingLandingResearch,
    pendingPlan,
    setPendingPlanForReview,
  ])

  useEffect(() => {
    if (!pendingLandingResearch) {
      mobileAutoStartKeyRef.current = null
    }
  }, [pendingLandingResearch])

  useEffect(() => {
    if (!isMobile || !pendingLandingResearch || pendingPlan || isStartingResearch) return

    const autoStartKey = JSON.stringify(pendingLandingResearch)
    if (mobileAutoStartKeyRef.current === autoStartKey) return

    mobileAutoStartKeyRef.current = autoStartKey
    void startPendingLandingResearch()
  }, [isMobile, isStartingResearch, pendingLandingResearch, pendingPlan, startPendingLandingResearch])

  useEffect(() => {
    if (!isMobile) return

    const handleRetryAutoStart = () => {
      if (!pendingLandingResearch || pendingPlan || isStartingResearch) return
      void startPendingLandingResearch()
    }

    window.addEventListener('research:retryAutoStart', handleRetryAutoStart)
    return () => window.removeEventListener('research:retryAutoStart', handleRetryAutoStart)
  }, [isMobile, isStartingResearch, pendingLandingResearch, pendingPlan, startPendingLandingResearch])

  const handleCancelPlan = async (reportId?: string) => {
    const cancelReportId =
      reportId ||
      pendingPlan?.reportId ||
      activePlanWaitRef.current?.reportId ||
      activePlanStartRef.current?.reportId ||
      undefined

    if (cancelReportId && planViewStartRef.current > 0) {
      const planViewDuration = Math.floor((Date.now() - planViewStartRef.current) / 1000)
      analytics.trackPlanAbandoned({
        report_id: cancelReportId,
        query_length: pendingPlan?.query?.length ?? 0,
        plan_view_duration_seconds: planViewDuration,
        depth: pendingPlan?.depth || 'maximum',
        is_public: pendingPlan?.isPublic ?? false,
        reason: 'cancel_button',
      })
      planViewStartRef.current = 0
    }

    if (activePlanStartRef.current) {
      activePlanStartRef.current.cancelled = true
    }
    abortActivePlanWait()
    handleAutoStartConsumed()
    if (cancelReportId) {
      // Call backend to mark as cancelled
      try {
        await cancelIntelligenceReport(cancelReportId)
      } catch (error) {
        console.warn('Failed to cancel report on backend:', error)
        // Continue with local cleanup even if backend fails
      }
      
      // Remove the cancelled run from intelligenceRuns to stop any polling
      setIntelligenceRuns(prev => prev.filter(run => run.reportId !== cancelReportId))
      void refreshBillingStatus()
      
      // Cancelled drafts no longer move into a top-level failed tab.
      setStudies(prev => prev.filter(study => {
        const isMatchingStudy = study.task_id === cancelReportId || 
          study.id === `ia-${cancelReportId}` ||
          (typeof study.id === 'string' && study.id.includes(cancelReportId))
        return !isMatchingStudy
      }))
      setRecoverablePendingPlans(prev => prev.filter(plan => plan.reportId !== cancelReportId))
      setStatusCounts(prev => ({
        ...prev,
        drafts: Math.max(0, prev.drafts - 1),
      }))
      setRecoveringPendingPlanId(current => (current === cancelReportId ? null : current))
    }
    
    setPendingPlan(null)
    setPlanConfirming(false)
    setIsStartingResearch(false)
    setPlanLoadingQuery(null)
  }

  // Direct approval function for InlineResearchFlow (doesn't rely on pendingPlan state)
  const handleApprovePlanDirect = async (plan: ResearchPlan, reportId: string, query: string, isPublic: boolean, depth: ResearchDepth = 'standard', updatedPersona?: string) => {
    console.log(`🚀 Approving plan with depth: ${depth}${updatedPersona ? `, persona changed to: ${updatedPersona}` : ''}`)
    try {
      await confirmIntelligencePlan(reportId, plan, updatedPersona)
      const newRun: IntelligenceRunSummary = {
        reportId,
        planSummary: plan.plan_summary,
        personaMode: updatedPersona || plan.inputs.persona_mode,
        status: 'queued',
        query,
        createdAt: new Date().toISOString(),
      }
      setIntelligenceRuns(prev => [newRun, ...prev.filter(run => run.reportId !== reportId)])

      const optimisticStudy: ResearchStudy = {
        id: `ia-${reportId}`,
        query,
        model: 'chatgpt',
        status: 'pending',
        isPublic,
        createdAt: new Date(),
        author: user ? { name: user.name, email: user.email, avatar_url: user.avatar_url } : undefined,
      }
      // Filter out any existing study with the same ID to prevent duplicates
      setStudies(prev => [optimisticStudy, ...prev.filter(s => s.id !== optimisticStudy.id && s.task_id !== reportId)])
      setStatusCounts(prev => ({
        ...prev,
        in_progress: prev.in_progress + 1,
      }))
      focusPendingTab()
      void refreshBillingStatus()

      enqueueToast({
        type: 'success',
        title: 'Research Started',
        description: 'Your research is now being processed. This typically takes 60-80 minutes.',
      })
      analytics.trackPlanApproved({
        report_id: reportId,
        query_length: query.length,
        is_public: isPublic,
        depth,
      })
      analytics.trackResearchStarted({
        research_id: reportId,
        report_id: reportId,
        query: query.substring(0, 100),
        query_length: query.length,
        is_public: isPublic,
        model: 'chatgpt',
        nth_research: studies.length + 1,
        is_first_research: studies.length === 0,
      })
    } catch (error) {
      console.error('Failed to confirm plan:', error)
      throw error
    }
  }

  const handleApprovePlan = async (updatedPersona?: string) => {
    if (!pendingPlan) return
    setPlanConfirming(true)
    try {
      await confirmIntelligencePlan(pendingPlan.reportId, pendingPlan.plan, updatedPersona)
      const newRun: IntelligenceRunSummary = {
        reportId: pendingPlan.reportId,
        planSummary: pendingPlan.plan.plan_summary,
        personaMode: updatedPersona || pendingPlan.plan.inputs.persona_mode,
        status: 'queued',
        query: pendingPlan.query,
        createdAt: new Date().toISOString(),
      }
      setIntelligenceRuns(prev => [newRun, ...prev.filter(run => run.reportId !== pendingPlan.reportId)])

      const optimisticStudy: ResearchStudy = {
        id: `ia-${pendingPlan.reportId}`,
        query: pendingPlan.query,
        model: 'chatgpt',
        status: 'pending',
        createdAt: new Date(),
        task_id: pendingPlan.reportId,
        isPublic: pendingPlan.isPublic,
        author: user ? { name: user.name, email: user.email, avatar_url: user.avatar_url } : undefined,
      }
      // Filter out any existing study with the same ID to prevent duplicates
      setStudies(prev => [optimisticStudy, ...prev.filter(s => s.id !== optimisticStudy.id && s.task_id !== pendingPlan.reportId)])
      setRecoverablePendingPlans(prev => prev.filter(plan => plan.reportId !== pendingPlan.reportId))
      setStatusCounts(prev => ({
        ...prev,
        drafts: Math.max(0, prev.drafts - 1),
        in_progress: prev.in_progress + 1,
      }))
      focusPendingTab()
      setPendingPlan(null)
      void refreshBillingStatus()
      enqueueToast({
        type: 'success',
        title: 'Plan Approved',
        description: 'The orchestrator is now executing your intelligence package.',
      })
      analytics.trackPlanApproved({
        report_id: pendingPlan.reportId,
        query_length: pendingPlan.query.length,
        is_public: pendingPlan.isPublic,
        depth: pendingPlan.depth,
      })
      analytics.trackResearchStarted({
        research_id: pendingPlan.reportId,
        report_id: pendingPlan.reportId,
        query: pendingPlan.query.substring(0, 100),
        query_length: pendingPlan.query.length,
        is_public: pendingPlan.isPublic,
        model: 'chatgpt',
        nth_research: studies.length + 1,
        is_first_research: studies.length === 0,
      })
    } catch (error) {
      console.error('Plan approval failed:', error)
      const message = error instanceof Error ? error.message : 'Failed to approve plan'
      enqueueToast({
        type: 'error',
        title: 'Approval Failed',
        description: message,
      })
    } finally {
      setPlanConfirming(false)
      setIsStartingResearch(false)
    }
  }

  const openCompletedStudy = useCallback(async (
    study: ResearchStudy,
    options?: { trackView?: boolean }
  ) => {
    setIsLoadingStudyDetails(true)
    const canonicalReportId = getCanonicalReportId(study)
    const shouldTrackView = options?.trackView !== false

    if (shouldTrackView) {
      analytics.trackResearchViewed({
        research_id: study.id,
        report_id: canonicalReportId,
        source: 'research',
        author_is_self: true,
        category: study.category,
      })
      // Use the numeric research_studies.id — for intelligence reports study.id is "ia-report_xxx"
      // so we fall back to study.researchId which is always the correct numeric DB id
      const numericResearchId = study.researchId ?? (typeof study.id === 'number' ? study.id : Number(study.id))
      if (!isNaN(numericResearchId) && numericResearchId > 0) {
        Promise.resolve(recordResearchView(numericResearchId, 'web')).catch(() => {})
      }
    }

    const isIntelligenceStudy = Boolean(study.isIntelligenceReport && canonicalReportId)

    if (!isIntelligenceStudy) {
      setShowProgressOverlay(false)
      setIntelligenceReportData(null)
      setShowCockpit(false)
      setSelectedStudy(study)
      setShowLegacyViewer(Boolean(study.results))
      setIsLoadingStudyDetails(false)
      return
    }

    const reportId = canonicalReportId as string
    try {
      const response = await fetch(`${API_BASE_URL}/api/intelligence/report/${reportId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      })

      if (!response.ok) {
        console.warn('Failed to fetch intelligence report:', response.status)
        setIsLoadingStudyDetails(false)
        return
      }

      const normalizedReportData = normalizeIntelligenceReportData(await response.json())
      const intelligenceStudyUpdate = buildIntelligenceReportStudy(study, normalizedReportData)
      const updatedStudy: ResearchStudy = {
        ...study,
        ...intelligenceStudyUpdate,
        model: study.model,
        status: study.status,
        createdAt: study.createdAt,
        completedAt:
          intelligenceStudyUpdate.completedAt instanceof Date
            ? intelligenceStudyUpdate.completedAt
            : study.completedAt,
        author: intelligenceStudyUpdate.author
          ? {
              ...intelligenceStudyUpdate.author,
              avatar_url: intelligenceStudyUpdate.author.avatar_url ?? undefined,
            }
          : study.author,
        podcast_status: intelligenceStudyUpdate.podcast_status ?? undefined,
        podcast_audio_ready: intelligenceStudyUpdate.podcast_audio_ready ?? undefined,
        podcast_duration_seconds: intelligenceStudyUpdate.podcast_duration_seconds ?? undefined,
        podcast_error_message: intelligenceStudyUpdate.podcast_error_message ?? undefined,
        results: {
          title: normalizedReportData.title || study.title || study.query || '',
          summary: normalizedReportData.summary || study.summary || '',
          content:
            normalizedReportData.written_report ||
            normalizedReportData.deep_research_report ||
            normalizedReportData.report_text ||
            '',
          keyFindings: normalizedReportData.key_findings || [],
          key_findings: normalizedReportData.key_findings || [],
          implications: [],
          sources: normalizedReportData.sources || [],
          report_text:
            normalizedReportData.written_report ||
            normalizedReportData.deep_research_report ||
            normalizedReportData.report_text ||
            '',
        },
      }
      setStudies(prev => prev.map(candidate => (
        candidate.id === study.id
          ? { ...candidate, ...updatedStudy }
          : candidate
      )))
      setSelectedStudy(updatedStudy)
      setShowProgressOverlay(false)
      setShowLegacyViewer(false)

      if (normalizedReportData.report_type === 'intelligence' && normalizedReportData.cig && normalizedReportData.sib) {
        setIntelligenceReportData(normalizedReportData)
        setShowCockpit(true)
        const targetHash = `#research/article/${study.id}`
        if (window.location.hash !== targetHash) {
          window.history.pushState({ tab: 'research', modal: 'research-article' }, '', targetHash)
        }
      }
    } catch (error) {
      console.error('Failed to fetch intelligence report:', error)
    } finally {
      setIsLoadingStudyDetails(false)
    }
  }, [])

  const openActiveProgressStudy = useCallback((study: ResearchStudy) => {
    const reportId = getCanonicalReportId(study)
    if (!reportId) {
      return
    }

    setIntelligenceReportData(null)
    setShowCockpit(false)
    setShowLegacyViewer(false)
    setSelectedStudy({
      ...study,
      task_id: study.task_id || reportId,
      intelligenceReportId: study.intelligenceReportId || reportId,
    })
    setShowProgressOverlay(true)
    setIsCancellingProgressStudy(false)
    setIntelligenceRuns((prev) => {
      const existingIndex = prev.findIndex((entry) => entry.reportId === reportId)
      const nextEntry: IntelligenceRunSummary = {
        reportId,
        planSummary: study.summary || '',
        personaMode: existingIndex === -1 ? '' : prev[existingIndex].personaMode,
        status: study.status,
        shipScore: existingIndex === -1 ? undefined : prev[existingIndex].shipScore,
        query: study.query,
        createdAt: study.createdAt.toISOString(),
      }

      if (existingIndex === -1) {
        return [nextEntry, ...prev]
      }

      return prev.map((entry, index) => (
        index === existingIndex
          ? { ...entry, ...nextEntry }
          : entry
      ))
    })
  }, [])

  const handleCancelActiveProgressStudy = useCallback(async () => {
    if (!selectedStudy) {
      return
    }

    const reportId = getCanonicalReportId(selectedStudy)
    if (!reportId) {
      return
    }

    setIsCancellingProgressStudy(true)
    try {
      await cancelIntelligenceReport(reportId)
      setIntelligenceRuns(prev => prev.filter(run => run.reportId !== reportId))
      setStudies(prev => prev.filter(study => {
        const isMatchingStudy = study.task_id === reportId ||
          study.id === `ia-${reportId}` ||
          (typeof study.id === 'string' && study.id.includes(reportId))
        return !isMatchingStudy
      }))
      setShowProgressOverlay(false)
      setSelectedStudy(null)
      enqueueToast({
        type: 'success',
        title: 'Research Cancelled',
        description: 'The active report has been cancelled.',
      })
      refreshActiveResearchBucket()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to cancel research right now.'
      enqueueToast({
        type: 'error',
        title: 'Cancellation Failed',
        description: message,
      })
    } finally {
      setIsCancellingProgressStudy(false)
    }
  }, [selectedStudy, refreshActiveResearchBucket, enqueueToast])

  useEffect(() => {
    if (!showProgressOverlay || !selectedStudy) {
      return
    }

    if (selectedStudy.status === 'completed') {
      const timeoutId = window.setTimeout(() => {
        void openCompletedStudy(selectedStudy, { trackView: false })
      }, 800)
      return () => window.clearTimeout(timeoutId)
    }

    if (selectedStudy.status === 'failed' || selectedStudy.status === 'cancelled') {
      setShowProgressOverlay(false)
      setSelectedStudy(null)
    }
  }, [openCompletedStudy, selectedStudy, showProgressOverlay])

  const handleStudyClick = async (study: ResearchStudy) => {
    engagementTracking.trackCardClicked()

    if (isActiveIntelligenceStudy(study)) {
      openActiveProgressStudy(study)
      return
    }

    if (study.status !== 'completed') return

    await openCompletedStudy(study)
  }

  const handlePrivacyToggle = async (e: React.MouseEvent, study: ResearchStudy) => {
    e.stopPropagation() // Prevent triggering handleStudyClick
    
    if (!user) {
      enqueueToast({
        type: 'error',
        title: 'Authentication Required',
        description: 'Please log in to change research privacy.'
      })
      return
    }

    try {
      const researchId = study.researchId ?? parseInt(study.id)
      if (!researchId || isNaN(researchId)) {
        enqueueToast({
          type: 'error',
          title: 'Invalid Research ID',
          description: 'Could not find a valid research ID for this study.'
        })
        return
      }

      const newIsPublic = !study.isPublic

      // Optimistically update UI
      setStudies(prev => prev.map(s => 
        s.id === study.id ? { ...s, isPublic: newIsPublic } : s
      ))

      const success = await updateResearchPrivacy(researchId, newIsPublic)
      
      if (success) {
        analytics.trackResearchPrivacyToggled({
          research_id: String(researchId),
          report_id: study.intelligenceReportId || undefined,
          new_state: newIsPublic ? 'public' : 'private',
        })
        enqueueToast({
          type: 'success',
          title: 'Privacy Updated',
          description: `Research is now ${newIsPublic ? 'public' : 'private'}.`
        })
      } else {
        // Revert on failure
        setStudies(prev => prev.map(s => 
          s.id === study.id ? { ...s, isPublic: study.isPublic } : s
        ))
        enqueueToast({
          type: 'error',
          title: 'Update Failed',
          description: 'Failed to update research privacy.'
        })
      }
    } catch (error) {
      console.error('Failed to toggle research privacy:', error)
      // Revert on error
      setStudies(prev => prev.map(s => 
        s.id === study.id ? { ...s, isPublic: study.isPublic } : s
      ))
      enqueueToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to change research privacy.'
      })
    }
  }

  const getStatusIcon = (status: ResearchStudy['status']) => {
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    
    switch (status) {
      case 'pending':
      case 'queued':
        return (
          <div className="relative flex items-center justify-center w-2 h-2">
            <div className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full" />
            <div className={`absolute w-2 h-2 bg-yellow-400 rounded-full ${isMobileDevice ? 'opacity-30' : 'animate-breathing opacity-30'}`} />
          </div>
        )
      case 'in_progress':
        return (
          <div className={`w-2 h-2 bg-blue-400 rounded-full border border-blue-200 ${isMobileDevice ? '' : 'animate-spin'}`} />
        )
      case 'podcast_pending':
        return (
          <div className={`w-2 h-2 bg-violet-400 rounded-full border border-violet-200 ${isMobileDevice ? '' : 'animate-pulse'}`} />
        )
      case 'interrupted':
        return (
          <div className={`w-2 h-2 bg-amber-400 rounded-full border border-amber-200 ${isMobileDevice ? '' : 'animate-pulse'}`} />
        )
      case 'completed':
        return (
          <div className="w-2 h-2 bg-green-400 rounded-full" />
        )
      case 'failed':
        return (
          <div className="w-2 h-2 bg-red-400 rounded-full" />
        )
    }
  }

  const getStatusText = (status: ResearchStudy['status']) => {
    switch (status) {
      case 'pending': return 'Pending'
      case 'queued': return 'Queued'
      case 'in_progress': return 'Researching'
      case 'podcast_pending': return 'Finalizing podcast...'
      case 'interrupted': return 'Resuming...'
      case 'completed': return 'Completed'
      case 'failed': return 'Failed'
    }
  }

  // Debug: ensure we don't hide all results due to unexpected statuses
  if (studies.length > 0) {
    const statusSet = Array.from(new Set(studies.map(s => s.status)))
    // console.log('[ResearchPage] studies loaded:', studies.length, 'statuses:', statusSet)
  }

  const availableTabs = useMemo(() => (
    RESEARCH_TAB_DEFINITIONS.filter(tab => tab.value !== 'drafts' || statusCounts.drafts > 0)
  ), [statusCounts.drafts])
  // Exclude non-displayable items from non-draft tabs.
  // planning/awaiting_confirmation belong in the Drafts tab.
  // cancelled items should never appear in any tab.
  const HIDDEN_IN_TABS = ['planning', 'awaiting_confirmation', 'cancelled']
  const visibleStudies = statusFilter === 'drafts'
    ? []
    : studies.filter(s => !HIDDEN_IN_TABS.includes(s.status as string))
  const visibleDrafts = statusFilter === 'drafts' ? recoverablePendingPlans : []
  const activeTabResultCount = statusFilter === 'drafts' ? visibleDrafts.length : visibleStudies.length

  const openResearchComposer = useCallback(() => {
    if (isStartingResearch) return
    setShowResearchModal(true)
    engagementTracking.trackModalOpened()
  }, [engagementTracking, isStartingResearch])
  
  const loadMoreStudies = useCallback(() => {
    if (!user || isLoadingMoreStudies || !hasMoreStudies) {
      return
    }

    setIsLoadingMoreStudies(true)
    ;(async () => {
      try {
        await loadResearchBucketPage({
          bucket: statusFilter,
          offset: loadedServerItemCount,
          append: true,
          cacheBuster: `${Date.now()}`,
        })
      } catch (error) {
        console.error('Failed to load more studies:', error)
        enqueueToast({
          type: 'error',
          title: 'Load More Failed',
          description: 'Unable to load more research right now.',
        })
      } finally {
        setIsLoadingMoreStudies(false)
      }
    })()
  }, [enqueueToast, hasMoreStudies, isLoadingMoreStudies, loadResearchBucketPage, loadedServerItemCount, statusFilter, user])

  // Helper function to format timestamp as relative time
  const formatRelativeTime = (date: Date | null | undefined): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return 'recently'
    }
    
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    
    if (seconds < 60) return 'just now'
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`
    
    // For older dates, show the actual date
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    })
  }

  const draftsTabSection = statusFilter === 'drafts' ? (
    <section
      data-testid="drafts-tab-panel"
      className={isMobile ? 'space-y-3' : 'mb-6 space-y-3'}
    >
      <div className="rounded-3xl border border-amber-200/70 bg-amber-50/80 p-4 shadow-sm backdrop-blur dark:border-amber-900/60 dark:bg-amber-950/25">
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-amber-700 dark:text-amber-300">
          Drafts
        </p>
        <h3 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
          {visibleDrafts.length === 1
            ? '1 plan is waiting for your review'
            : `${visibleDrafts.length} plans are waiting for your review`}
        </h3>
        <p className="mt-2 text-sm leading-6 text-amber-900/80 dark:text-amber-100/80">
          These plans were created successfully. Review them to continue the research run, or cancel them to release the reserved quota.
        </p>

        <div className="mt-4 space-y-3">
          {visibleDrafts.map(report => {
            const reviewLabel = report.status === 'awaiting_confirmation' ? 'Review plan' : 'Check plan'
            const reviewingThisPlan = recoveringPendingPlanId === report.reportId

            return (
              <div
                key={report.reportId}
                data-testid={`recoverable-pending-plan-${report.reportId}`}
                className="rounded-2xl border border-amber-200/80 bg-white/90 p-4 shadow-sm dark:border-amber-900/60 dark:bg-slate-900/70"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
                      <span
                        className={`rounded-full px-2.5 py-1 ${
                          report.status === 'awaiting_confirmation'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
                        }`}
                      >
                        {report.status === 'awaiting_confirmation' ? 'Plan ready' : 'Plan generating'}
                      </span>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        {report.isPublic ? 'Public' : 'Private'}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        Updated {formatRelativeTime(report.updatedAt)}
                      </span>
                    </div>
                    <h4 className="mt-3 text-base font-semibold text-gray-900 dark:text-white">
                      {report.title}
                    </h4>
                    {report.title !== report.query && (
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        {report.query}
                      </p>
                    )}
                    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                      {report.statusMessage || (
                        report.status === 'awaiting_confirmation'
                          ? 'Your plan is ready for approval and the billing reservation is waiting on your decision.'
                          : 'This draft is still preparing. You can keep checking it here or cancel it if you no longer need it.'
                      )}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      data-testid={`review-pending-plan-${report.reportId}`}
                      onClick={() => {
                        void handleReviewRecoverablePlan(report)
                      }}
                      disabled={reviewingThisPlan}
                      className="inline-flex items-center justify-center rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-wait disabled:opacity-70 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                    >
                      {reviewingThisPlan ? 'Opening...' : reviewLabel}
                    </button>
                    <button
                      type="button"
                      data-testid={`cancel-pending-plan-${report.reportId}`}
                      onClick={() => {
                        void handleCancelPlan(report.reportId)
                      }}
                      disabled={reviewingThisPlan}
                      className="inline-flex items-center justify-center rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        {hasMoreStudies && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={loadMoreStudies}
              disabled={isLoadingMoreStudies}
              className="rounded-full border border-amber-300/80 px-4 py-2 text-sm font-medium text-amber-900 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-800 dark:text-amber-100 dark:hover:bg-amber-900/30"
            >
              {isLoadingMoreStudies ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>
    </section>
  ) : null
  const totalVisibleResearchCount = Math.max(
    statusCounts.completed + statusCounts.in_progress + statusCounts.drafts,
    // Fall back to the actual loaded items when server counts are absent or zero,
    // so the first-time onboarding gate never fires when real studies are loaded.
    visibleStudies.length + visibleDrafts.length
  )
  const hasNoResearchHistory = isMobile && user?.research_history_count === 0
  const showMobileFirstTimeOnboarding =
    isMobile &&
    statusFilter === 'completed' &&
    totalVisibleResearchCount === 0 &&
    (hasNoResearchHistory || !isLoadingStudies)

  useEffect(() => {
    if (!showMobileFirstTimeOnboarding) {
      return
    }
  }, [activeTabResultCount, hasNoResearchHistory, isLoadingStudies, showMobileFirstTimeOnboarding, statusFilter, totalVisibleResearchCount, user?.id, user?.research_history_count])

  useEffect(() => {
    visibleStudies.forEach((study) => {
      const trackingId = study.intelligenceReportId || study.researchId?.toString() || study.id
      if (!trackingId || trackedStudyCardIdsRef.current.has(trackingId)) return

      trackedStudyCardIdsRef.current.add(trackingId)
      engagementTracking.trackCardViewed()
    })
  }, [engagementTracking, visibleStudies])

  useEffect(() => {
    if (statusFilter === 'drafts' && statusCounts.drafts === 0) {
      setStatusFilter('completed')
    }
  }, [statusCounts.drafts, statusFilter])

  // Debug mode minimal render
  if (debugMode && isMobile) {
    return (
      <div className="min-h-screen p-4">
        <div className="bg-yellow-100 border border-yellow-400 rounded p-4 mb-4">
          <p className="font-semibold">Debug Mode Active</p>
          <p className="text-sm">To disable: Run in console: localStorage.removeItem('researchPageDebugMode')</p>
        </div>
        <h1 className="text-2xl font-bold mb-4">Research Page (Debug)</h1>
        <div className="space-y-2 mb-4">
          <p>Studies loaded: {studies.length}</p>
          <p>Visible studies: {visibleStudies.length}</p>
          <p>Visible drafts: {visibleDrafts.length}</p>
          <p>User: {user ? 'Logged in' : 'Not logged in'}</p>
          <p>API: {apiHealthy ? 'Healthy' : 'Unhealthy'}</p>
          <p>WebSocket: {wsConnected ? 'Connected' : 'Disconnected'}</p>
        </div>
        <button 
          onClick={() => {
            localStorage.removeItem('researchPageDebugMode')
            window.location.reload()
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded"
        >
          Exit Debug Mode
        </button>
      </div>
    )
  }

  return (
    <div className={`min-h-screen ${isMobile ? '' : 'px-8 py-12'}`}>
      {/* Mobile: Full-screen plan experience */}
      {isMobile && isStartingResearch && planLoadingQuery && !pendingPlan && (
        <IntelligencePlanModal
          mode="loading"
          query={planLoadingQuery}
          onCancel={() => {
            void handleCancelPlan()
          }}
        />
      )}
      {isMobile && pendingPlan && (
        <IntelligencePlanModal
          mode="review"
          plan={pendingPlan.plan}
          isConfirming={planConfirming}
          onPlanChange={updatedPlan =>
            setPendingPlan((current: PendingPlanState | null) =>
              current ? { ...current, plan: updatedPlan } : current
            )
          }
          onCancel={handleCancelPlan}
          onConfirm={handleApprovePlan}
        />
      )}
      {!isMobile && pendingPlan && (
        <IntelligencePlanModal
          mode="review"
          plan={pendingPlan.plan}
          isConfirming={planConfirming}
          onPlanChange={updatedPlan =>
            setPendingPlan((current: PendingPlanState | null) =>
              current ? { ...current, plan: updatedPlan } : current
            )
          }
          onCancel={handleCancelPlan}
          onConfirm={handleApprovePlan}
        />
      )}
      <div className={`mx-auto ${isMobile ? 'max-w-full' : 'max-w-4xl'}`}>
        
        {/* Header - Mobile optimized */}
        {isMobile ? (
          !showMobileFirstTimeOnboarding ? (
            <PageHeader
              title="Research"
              action={
                <button
                  onClick={openResearchComposer}
                  disabled={isStartingResearch}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isStartingResearch
                      ? 'bg-gray-300 text-gray-600 cursor-not-allowed dark:bg-gray-700 dark:text-gray-300'
                      : 'bg-black text-white hover:bg-gray-800'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>{isStartingResearch ? 'Generating...' : 'New Research'}</span>
                </button>
              }
            />
          ) : null
        ) : (
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
              Deep Research
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Conduct deep research with AI-powered analysis
            </p>
          </div>
        )}

        {/* Intelligence runs are now integrated into the In Progress section via optimistic studies */}

        {/* Mobile Optimized Layout */}
        {isMobile ? (
          showMobileFirstTimeOnboarding ? (
            <MobileResearchWelcomeState
              onCreateResearch={openResearchComposer}
              disabled={isStartingResearch}
            />
          ) : (
          <div className="pb-4">
            {/* Filter Tabs - Glass Design */}
            <div className="px-4 pt-4">
              <div className="flex gap-2 p-1 bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30">
                {availableTabs.map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => {
                      setStatusFilter(filter.value)
                      engagementTracking.trackFilterChange()
                    }}
                    className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      statusFilter === filter.value
                        ? 'bg-black text-white dark:bg-white dark:text-black shadow-lg'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100/50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <span>{filter.mobileLabel ?? filter.label}</span>
                    <span className="ml-2 text-xs opacity-70">{statusCounts[filter.value]}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Research List */}
            <div className="px-4 pt-4 space-y-3 pb-20">
              {isLoadingStudies ? (
                <div className="text-center py-16">
                  <div className="flex justify-center mb-4">
                    <LoadingAnimation size={64} />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">Loading your research...</p>
                </div>
              ) : activeTabResultCount === 0 ? (
                showMobileFirstTimeOnboarding ? (
                  <MobileResearchWelcomeState
                    onCreateResearch={openResearchComposer}
                    disabled={isStartingResearch}
                  />
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                      {statusFilter === 'drafts'
                        ? 'No draft plans waiting for review'
                        : statusFilter === 'in_progress'
                          ? 'No active research'
                          : 'No completed research yet'}
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                      {statusFilter === 'completed'
                        ? 'Start your first research above'
                        : statusFilter === 'drafts'
                          ? 'New drafts will appear here when a plan is waiting on your review.'
                          : 'Try a different filter or start new research'}
                    </p>
                  </div>
                )
              ) : statusFilter === 'drafts' ? (
                draftsTabSection
              ) : (
                <>
                  {visibleStudies.map((study) => (
                    <div
                      key={study.id}
                      data-testid={`research-study-card-${study.id}`}
                      onClick={() => handleStudyClick(study)}
                      className={`bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 ${
                        isClickableStudyCard(study) ? 'cursor-pointer active:scale-[0.98]' : ''
                      } transition-all`}
                    >
                      {/* Header with badges and timestamp */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(study.status)}
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            {getStatusText(study.status)}
                          </span>
                          {/* Deep Analysis Badge - standard depth (subtle indigo) */}
                          {study.status === 'completed' && study.isIntelligenceReport && study.depth !== 'maximum' && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                              Deep Analysis
                            </span>
                          )}
                          {/* Expert Report Badge - maximum depth (subtle emerald) */}
                          {study.status === 'completed' && study.depth === 'maximum' && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                              Expert Report
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatRelativeTime(study.createdAt)}
                        </span>
                      </div>
                      
                      {/* Title */}
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                        {getResearchTitle(study)}
                      </h3>
                      
                      {/* Summary Preview */}
                      {getValidSummary(study) && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                          {getValidSummary(study)}
                        </p>
                      )}
                      
                      {/* Footer Info */}
                      {(study.status === 'completed' || study.status === 'failed') && (
                        <div className={`flex items-center ${study.status === 'completed' ? 'justify-start' : 'justify-end'}`}>
                          {study.status === 'completed' && (
                            <span 
                              data-testid={`research-privacy-badge-${study.id}`}
                              className={`text-xs px-2 py-0.5 rounded-full ${
                              study.isPublic ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {study.isPublic ? 'Public' : 'Private'}
                            </span>
                          )}
                          
                          {study.status === 'failed' && (study.intelligenceReportId || study.researchId) && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); retryFailedResearch(study) }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm hover:shadow-md hover:from-blue-700 hover:to-indigo-700 transition-all"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 4l-6 6M4 20l6-6" />
                                </svg>
                                Retry
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Modern Progress Section for in-flight studies */}
                      {(study.status === 'in_progress' || study.status === 'pending' || study.status === 'queued' || study.status === 'interrupted' || study.status === 'podcast_pending') && (
                        <div className="mt-4 space-y-3">
                          {(() => {
                            const phaseInfo = study.isIntelligenceReport
                              ? getProgressPresentation(study)
                              : {
                                  label: study.status === 'pending' || study.status === 'queued'
                                    ? 'Starting research...'
                                    : 'Processing...',
                                  progress: study.progress ?? 5,
                                  phaseNum: 1,
                                }
                            const displayProgress = phaseInfo.progress

                            return (
                              <>
                                <div className="flex items-center gap-3">
                                  <span className="relative flex h-2.5 w-2.5">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
                                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-500" />
                                  </span>
                                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                    {phaseInfo.label}
                                  </span>
                                </div>
                                
                                {/* Progress Bar with Gradient */}
                                <div className="relative">
                                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                    <div 
                                      className="h-full rounded-full bg-gradient-to-r from-blue-500 via-blue-400 to-indigo-500 transition-all duration-700 ease-out relative overflow-hidden"
                                      style={{ width: `${Math.min(100, Math.max(5, displayProgress))}%` }}
                                    >
                                      {/* Shimmer effect */}
                                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                                    </div>
                                  </div>
                                  {/* Progress percentage */}
                                  <div className="mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                    <span>Phase {phaseInfo.phaseNum} of 6</span>
                                    <span>{Math.round(displayProgress)}%</span>
                                  </div>
                                </div>
                                {isActiveIntelligenceStudy(study) ? (
                                  <p className="text-[11px] font-medium text-blue-600 dark:text-blue-400">
                                    {isMobile ? 'Tap to view live progress' : 'Open live progress view'}
                                  </p>
                                ) : null}
                              </>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Load More Button */}
                  {hasMoreStudies && (
                    <div className="flex justify-center pt-4">
                      <button
                        onClick={loadMoreStudies}
                        disabled={isLoadingMoreStudies}
                        className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        {isLoadingMoreStudies ? 'Loading...' : 'Load More'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          )
        ) : (
          <>
            {/* Desktop: Unified Inline Research Flow */}
            <div className="mb-12 max-w-4xl mx-auto">
              <InlineResearchFlow
                onSubmit={handleStartResearchWithResult}
                onConfirm={handleApprovePlanDirect}
                onCancel={handleCancelPlan}
                disabled={isStartingResearch}
                initialQuery={pendingLandingResearch?.query}
                initialDepth={pendingLandingResearch?.depth}
                initialIsPublic={pendingLandingResearch?.isPublic}
                autoStart={!!pendingLandingResearch}
                onAutoStartConsumed={handleAutoStartConsumed}
              />
            </div>

            {/* Research Studies Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`font-semibold text-gray-900 dark:text-white ${isMobile ? 'text-lg' : 'text-xl'}`}>
                  Research Studies
                </h2>
                
                <div className="flex items-center gap-4">
                  {/* Manual Refresh Button for Debugging */}
                  {studies.some(s => !isPlanOnlyIntelligenceStudy(s) && (s.status === 'pending' || s.status === 'queued' || s.status === 'in_progress' || s.status === 'interrupted')) && (
                    <button
                      onClick={handleManualRefresh}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Manually refresh research status"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                  )}
                  
                  {/* Status Filter - Shared tabs */}
                  <div className="flex items-center bg-white dark:bg-gray-800 rounded-2xl p-1 shadow-sm border border-gray-200 dark:border-gray-700">
                    {availableTabs.map((filter) => (
                      <button
                        key={filter.value}
                        onClick={() => {
                          setStatusFilter(filter.value)
                          engagementTracking.trackFilterChange()
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                          statusFilter === filter.value
                            ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {filter.value === 'completed' ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : filter.value === 'in_progress' ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        )}
                        {filter.label}
                        {statusCounts[filter.value] > 0 && (
                          <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                            statusFilter === filter.value 
                              ? 'bg-white/20 text-white' 
                              : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                          }`}>
                            {statusCounts[filter.value]}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {statusFilter === 'drafts' ? draftsTabSection : null}
              {isLoadingStudies ? (
                <div className="text-center py-16">
                  <div className="flex justify-center mb-4">
                    <LoadingAnimation size={64} />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">Loading your research...</p>
                </div>
              ) : activeTabResultCount === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    {statusFilter === 'drafts'
                      ? 'No draft plans waiting for review'
                      : statusFilter === 'in_progress'
                        ? 'No research in progress'
                        : 'No completed research yet'}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    {statusFilter === 'completed'
                      ? 'Start your first research above'
                      : statusFilter === 'drafts'
                        ? 'New drafts will appear here when a plan is waiting on your review.'
                        : 'Try a different filter or start new research'}
                  </p>
                </div>
              ) : statusFilter === 'drafts' ? null : (
                <div className={isMobile ? "space-y-0" : "space-y-4"}>
                  {visibleStudies.map((study) => (
                    <div
                      key={study.id}
                      data-testid={`research-study-card-${study.id}`}
                      onClick={() => handleStudyClick(study)}
                      className={isMobile ? `
                        p-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 
                        ${isClickableStudyCard(study) ? 'cursor-pointer active:bg-gray-50 dark:active:bg-gray-800' : 'cursor-default'}
                      ` : `
                        p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl 
                        hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200 
                        ${isClickableStudyCard(study) ? 'cursor-pointer hover:shadow-md' : 'cursor-default'}
                      `}
                    >
                      {isMobile ? (
                        // Mobile layout - similar to Explore feed
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(study.status)}
                              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                {getStatusText(study.status)}
                              </span>
                              {/* Deep Analysis Badge - standard depth (subtle indigo) */}
                              {study.status === 'completed' && study.isIntelligenceReport && study.depth !== 'maximum' && (
                                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                                  Deep Analysis
                                </span>
                              )}
                              {/* Expert Report Badge - maximum depth (subtle emerald) */}
                              {study.status === 'completed' && study.depth === 'maximum' && (
                                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                  Expert Report
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {formatRelativeTime(study.createdAt)}
                            </span>
                          </div>
                          
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
                            {getResearchTitle(study)}
                          </h3>
                          
                          {getValidSummary(study) && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                              {getValidSummary(study)}
                            </p>
                          )}
                          
                          {study.status === 'completed' && (
                            <div className="flex items-center justify-end">
                              <span
                                data-testid={`research-privacy-badge-${study.id}`}
                                className={`text-xs px-2 py-1 rounded-full ${
                                study.isPublic ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                                {study.isPublic ? 'Public' : 'Private'}
                              </span>
                            </div>
                          )}
                          
                          {/* Modern Progress Section for In-Progress/Pending/Queued (Mobile) */}
                          {(study.status === 'in_progress' || study.status === 'pending' || study.status === 'queued' || study.status === 'interrupted') && (
                            <div className="mt-3 space-y-2">
                              {/* Phase Indicator */}
                              <div className="flex items-center gap-2">
                                <span className="relative flex h-2 w-2">
                                  <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
                                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
                                </span>
                                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                  {(() => {
                                    if (study.status === 'pending' || study.status === 'queued') return 'Starting research...'
                                    const progress = study.progress ?? 0
                                    if (progress < 15) return 'Analyzing query...'
                                    if (progress < 30) return 'Collecting sources...'
                                    if (progress < 50) return 'Extracting evidence...'
                                    if (progress < 70) return 'Synthesizing insights...'
                                    if (progress < 85) return 'Building scenarios...'
                                    if (progress < 95) return 'Quality assurance...'
                                    return 'Generating report...'
                                  })()}
                                </span>
                              </div>
                              
                              {/* Progress Bar with Gradient */}
                              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                <div 
                                  className="h-full rounded-full bg-gradient-to-r from-blue-500 via-blue-400 to-indigo-500 transition-all duration-700 ease-out relative overflow-hidden"
                                  style={{ width: `${Math.min(100, Math.max(5, study.progress ?? 5))}%` }}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        // Desktop layout - existing style
                        <>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              {getStatusIcon(study.status)}
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {getStatusText(study.status)}
                              </span>
                              {/* Deep Analysis Badge - standard depth (subtle indigo) */}
                              {study.status === 'completed' && study.isIntelligenceReport && study.depth !== 'maximum' && (
                                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                                  Deep Analysis
                                </span>
                              )}
                              {/* Expert Report Badge - maximum depth (subtle emerald) */}
                              {study.status === 'completed' && study.depth === 'maximum' && (
                                <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                  Expert Report
                                </span>
                              )}
                              {/* Privacy Badge */}
                              {study.status === 'completed' && (
                                <span
                                  data-testid={`research-privacy-badge-${study.id}`}
                                  className={`text-xs px-2 py-1 rounded-full ${
                                  study.isPublic ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                }`}>
                                  {study.isPublic ? 'Public' : 'Private'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Privacy Toggle for Completed Research */}
                              {study.status === 'completed' && !study.isPublic && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handlePrivacyToggle(e, study)
                                  }}
                                  className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                  title="Make research public"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              )}
                              {/* Retry Button for Failed Research */}
                              {study.status === 'failed' && (study.intelligenceReportId || study.researchId) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); retryFailedResearch(study) }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm hover:shadow-md hover:from-blue-700 hover:to-indigo-700 transition-all"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M20 4l-6 6M4 20l6-6" />
                                  </svg>
                                  Retry
                                </button>
                              )}
                              <div className="text-right">
                                <span className="text-xs text-gray-400 dark:text-gray-500 block">
                                  {formatRelativeTime(study.createdAt)}
                                </span>
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  {study.createdAt instanceof Date && !isNaN(study.createdAt.getTime()) 
                                    ? study.createdAt.toLocaleTimeString([], {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })
                                    : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                          {getResearchTitle(study)}
                          </h3>
                          
                          {/* Modern Progress Section for in-flight studies */}
                          {(study.status === 'in_progress' || study.status === 'pending' || study.status === 'queued' || study.status === 'interrupted' || study.status === 'podcast_pending') && (
                            <div className="mt-4 space-y-3">
                              {(() => {
                                const phaseInfo = study.isIntelligenceReport
                                  ? getProgressPresentation(study)
                                  : {
                                      label: study.status === 'pending' || study.status === 'queued'
                                        ? 'Starting research...'
                                        : 'Processing...',
                                      progress: study.progress ?? 5,
                                      phaseNum: 1,
                                    }
                                const displayProgress = phaseInfo.progress

                                return (
                                  <>
                                    <div className="flex items-center gap-3">
                                      <span className="relative flex h-2.5 w-2.5">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping" />
                                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-500" />
                                      </span>
                                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                        {phaseInfo.label}
                                      </span>
                                    </div>
                                    
                                    {/* Progress Bar with Gradient */}
                                    <div className="relative">
                                      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                        <div 
                                          className="h-full rounded-full bg-gradient-to-r from-blue-500 via-blue-400 to-indigo-500 transition-all duration-700 ease-out relative overflow-hidden"
                                          style={{ width: `${Math.min(100, Math.max(5, displayProgress))}%` }}
                                        >
                                          {/* Shimmer effect */}
                                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                                        </div>
                                      </div>
                                      {/* Progress percentage */}
                                      <div className="mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                        <span>Phase {phaseInfo.phaseNum} of 6</span>
                                        <span>{Math.round(displayProgress)}%</span>
                                      </div>
                                    </div>
                                    {isActiveIntelligenceStudy(study) ? (
                                      <p className="text-[11px] font-medium text-blue-600 dark:text-blue-400">
                                        {isMobile ? 'Tap to view live progress' : 'Open live progress view'}
                                      </p>
                                    ) : null}
                                  </>
                                )
                              })()}
                            </div>
                          )}
                          
                          {study.status === 'completed' && (
                            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                              {getValidSummary(study) && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                                  {getValidSummary(study)}
                                </p>
                              )}
                              <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                                <span>Click to view full research</span>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                  
                  {/* Load More Button - works for both mobile and desktop */}
                  {hasMoreStudies && (
                    <div className="flex justify-center pt-4">
                      <button
                        onClick={loadMoreStudies}
                        disabled={isLoadingMoreStudies}
                        className="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        {isLoadingMoreStudies ? 'Loading...' : 'Load More'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Loading overlay while fetching research details */}
      {isLoadingStudyDetails && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl flex flex-col items-center">
            <LoadingAnimation size={48} />
            <p className="mt-4 text-gray-700 dark:text-gray-300">Loading research content...</p>
          </div>
        </div>
      )}

      {showProgressOverlay && selectedStudy && !showCockpit && (
        <IntelligenceProgressOverlay
          viewMode={viewMode}
          query={selectedStudy.query || selectedStudy.title || 'Research in progress'}
          status={selectedStudy.status}
          rawPhase={selectedStudy.currentPhase}
          createdAt={selectedStudy.createdAt}
          etaSeconds={selectedStudy.etaSeconds}
          stepLabel={selectedStudy.statusMessage}
          isCancelling={isCancellingProgressStudy}
          onClose={() => {
            setShowProgressOverlay(false)
            setSelectedStudy(null)
            setIsCancellingProgressStudy(false)
          }}
          onCancel={() => {
            void handleCancelActiveProgressStudy()
          }}
        />
      )}

      {/* Intelligence Cockpit View for new intelligence reports */}
      {showCockpit && selectedStudy && intelligenceReportData && (
        <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-gray-900">
          <div className={`h-full ${viewMode === 'mobile' ? '' : 'pl-24'} overflow-y-auto`}>
            <IntelligenceReportCockpit
              reportData={intelligenceReportData}
              title={selectedStudy.title || selectedStudy.query || 'Intelligence Report'}
              study={selectedStudy}
              onShowToast={onShowToast}
              onRequestDownload={() => {}}
              onBack={() => {
                setShowCockpit(false)
                setIntelligenceReportData(null)
                setSelectedStudy(null)
              }}
              viewMode={viewMode}
              hasFloatingAudio={hasFloatingAudio}
              currentUser={user ? { name: user.name, email: user.email, avatar_url: user.avatar_url } : null}
              onPrivacyToggle={async (newIsPublic: boolean) => {
                if (!user || !selectedStudy) return false
                try {
                  // Use researchId from study metadata (linked research_studies entry) for intelligence reports
                  const researchId = selectedStudy.researchId ?? parseInt(selectedStudy.id)
                  if (!researchId || isNaN(researchId)) {
                    console.error('[CockpitRuntimeWrapper] No valid researchId for privacy toggle', { id: selectedStudy.id, researchId: selectedStudy.researchId })
                    enqueueToast({
                      type: 'error',
                      title: 'Cannot Update Privacy',
                      description: 'This research is not yet indexed. Please try again later.'
                    })
                    return false
                  }
                  
                  const success = await updateResearchPrivacy(researchId, newIsPublic)
                  if (success) {
                    // Update local state
                    setStudies(prev => prev.map(s => 
                      s.id === selectedStudy.id ? { ...s, isPublic: newIsPublic } : s
                    ))
                    setSelectedStudy(prev => prev ? { ...prev, isPublic: newIsPublic } : null)
                    enqueueToast({
                      type: 'success',
                      title: 'Privacy Updated',
                      description: `Research is now ${newIsPublic ? 'public' : 'private'}.`
                    })
                  }
                  return success
                } catch (error) {
                  console.error('Privacy toggle failed:', error)
                  enqueueToast({
                    type: 'error',
                    title: 'Update Failed',
                    description: 'Failed to update research privacy.'
                  })
                  return false
                }
              }}
              onNavigateToProfile={onNavigateToProfile}
            />
          </div>
        </div>
      )}

      {showLegacyViewer && selectedStudy && !showCockpit && (
        <ResearchResultViewer
          study={selectedStudy}
          isOpen={showLegacyViewer}
          onClose={() => {
            setShowLegacyViewer(false)
            setSelectedStudy(null)
          }}
          viewMode={viewMode}
        />
      )}

      {/* Research Modal */}
      <ResearchModal
        isOpen={showResearchModal}
        onClose={() => setShowResearchModal(false)}
        onStartResearch={handleStartResearch}
        viewMode={viewMode}
      />
      {/* Upgrade modal for gated free users */}
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentPlan={billingPlan}
        billingStatus={billingStatus}
        trigger="limit_reached"
        userCreatedAt={user?.created_at}
        onUpgraded={() => {
          setShowUpgradeModal(false)
          void refreshBillingStatus()
          enqueueToast({
            type: 'success',
            title: 'Upgraded',
            description: 'Your account has been upgraded. You can continue researching without daily limits.'
          })
          // Re-trigger pending research if the user upgraded after a failed auto-start
          setTimeout(() => {
            window.dispatchEvent(new Event('research:retryAutoStart'))
          }, 300)
        }}
      />
      <OutOfScopeModal
        open={outOfScope.visible}
        message={outOfScope.message}
        onClose={() => setOutOfScope((prev) => ({ ...prev, visible: false }))}
        onTryAgain={() => {
          setOutOfScope((prev) => ({ ...prev, visible: false }))
          // Let interested surfaces (inline input / research modal) refocus.
          window.dispatchEvent(new Event('research:focusInput'))
        }}
      />
    </div>
  )
}
